import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { getToken } from "../utils/auth";
import configData from "../config.json";
import {
  createFolderRequest,
  deleteFolderRequest,
  fetchDeviceInfoCacheRequest,
  fetchFieldConfigRequest,
  fetchFolderAssignmentsRequest,
  fetchFoldersRequest,
  fetchMicromomentsRequest,
  moveDevicesRequest,
  restartFullyCacheRequest,
  sendDeviceCommandRequest,
  updateDeviceNotesRequest,
  updateMultipleDeviceNotesRequest,
} from "../functions/mdmApi";
import {
  buildAllowedK10Options,
  collapseFullySettingsCommands,
  collapseK10Commands,
  FULLY_SET_BOOLEAN_ENDPOINT,
  FULLY_SETTINGS_ENDPOINTS,
  fullySettingsOptions,
  K10_COMMAND_ENDPOINTS,
  K10_CSDK_SCHEDULE_ENDPOINTS,
  K10_CSDK_TOGGLE_ENDPOINTS,
  K10_LENOVO_SHUTDOWN_ENDPOINT,
  K10_LENOVO_DEVICE_MODEL,
  LOAD_APK_FILE_ENDPOINT,
  getActionKeyForEndpoint,
  getTextPayloadKeyForEndpoint,
  normalizeActionKey,
  normalizeCommandOptions,
  normalizeDeviceResult,
  normalizeStringOptions,
  parseSseEventBlock,
  DEVICE_INFO_NO_STREAM_ENDPOINT,
} from "../functions/mdmCommandUtils";
import {
  runFieldCommand,
  runMdmCommand,
  FIELD_DATABASE_UPDATE_ENDPOINTS,
  FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT,
  FIELD_SCAN_AND_GO_DEVICE_IDS_ENDPOINTS,
  FIELD_SCAN_AND_GO_RELEASE_DEVICE_ENDPOINT,
  FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT,
  FIELD_CLEAR_ALL_AND_UPDATE_ENDPOINT,
  FIELD_CHANGE_NCR_SERVER_ENDPOINT,
  FIELD_NCR_SERVER_OPTIONS,
  FIELD_FETCH_NCR_STATUS_ENDPOINT,
  FIELD_MULTI_SWAN_SONG_ENDPOINT,
  FIELD_TRIGGER_CAMERA_ENDPOINT,
  FIELD_TRIGGER_SCREENSHOT_ENDPOINT,
  FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT,
  FIELD_UPDATE_DATABASE_ENDPOINT,
  FIELD_UPDATE_DATABASE_NO_TIMESTAMP_ENDPOINT,
} from "../functions/mdmCommandRunners";
import {
  allDevicesFolderId,
  allRetailerDevicesFolderId,
  buildFolderOptions,
  buildFoldersById,
  getBranchKey,
  getBranchMountedFolders,
  getChildFolders,
  getDeviceDuplicateFolderPath,
  getDeviceCurrentFolderName,
  getDeviceCurrentFolderFilterValue,
  UNASSIGNED_FOLDER_FILTER_VALUE,
  getFolderFullLabel,
  getFolderKey,
  getBranchMountKeyFromFolderId,
  getDeviceHomeBranchKey,
  getRetailerMountedFolders,
  getRootMountedFolders,
  folderBelongsToBranchKey,
  parseAllDevicesFolderId,
  parseAllRetailerDevicesFolderId,
  parseBranchKey,
} from "../functions/mdmFolderUtils";
import {
  applyDefinedFilters,
  createDefaultDeviceFilters,
} from "../filters/deviceFilters";
import MdmSidebarNav from "../components/mdm/MdmSidebarNav";
import MdmFleetControls from "../components/mdm/MdmFleetControls";
import MdmDevicesTable from "../components/mdm/MdmDevicesTable";
import MdmCommandModal from "../components/mdm/MdmCommandModal";
import MdmDeviceInfoModal from "../components/mdm/MdmDeviceInfoModal";
import MdmLocationVersionWindow from "../components/mdm/MdmLocationVersionWindow";
import MdmFleetViewContextBar from "../components/mdm/MdmFleetViewContextBar";
import MdmDeviceNotesModal from "../components/mdm/MdmDeviceNotesModal";
import { DeviceSearchBar } from "../components/mdm/DeviceFilters";
import { exportCurrentViewToExcel as exportMdmCurrentViewToExcel } from "../functions/mdmExcelExport";
import { countJourneyStatuses, buildJourneyStatusByDeviceId } from "../functions/mdmFieldStatusUtils";
import {
  DEFAULT_ACTIVE_MANAGED_COLUMN_KEYS,
  MANAGEABLE_BASE_COLUMNS,
  OPTIONAL_PAYLOAD_CACHE_COLUMNS,
  OPTIONAL_TIME_COLUMNS,
  sanitizeActiveManagedColumnKeys,
} from "../functions/mdmColumnDefinitions";
import {
  fetchColumnPreferencesRequest,
  saveColumnPreferencesRequest,
} from "../functions/mdmColumnPreferencesApi";
import { refreshMdmDeviceInfoForDevices, refreshLogsLocationForDevices } from "../functions/mdmLocationVersionRefresh";
import {
  cleanSsid,
  extractLocationFromDeviceInfo,
  extractLocationFromLogData,
  flattenDevices,
  formatTime,
  getBatteryIcon,
  getScreenshotUrlFromResult,
  getStatus,
  normalizeTimestampMs,
  readDevicePayloadField,
  stringifyForTableCell,
  truncateText,
  buildFieldScreenshotDataFromScreenshots,
} from "../functions/mdmDeviceDisplayUtils";
import {
  findDevicePath,
  mergeDeviceResultsIntoCache,
  mergeFieldScreenshotResultsIntoCache,
  patchDeviceNotesInCache,
  patchMultipleDeviceNotesInCache,
} from "../functions/mdmDeviceCacheUtils";
import { resolveStoreMapEntry } from "../functions/branchMapboxUtils";
import {
  createFullFilterDefinitions,
  createOptionalPayloadCacheColumnSortDefinitions,
  createSortDefinitions,
} from "../functions/mdmDeviceSortFilterUtils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAdminPermission from "../Hooks/useAdminPermission";

const MDM_DEVICES_PAGE_SIZE = 200;

const MdmLanding = () => {
  const SERVER_URL = configData.SERVER_URL;
  const navigate = useNavigate();
  const isAdminUser = useAdminPermission();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFieldCommandModalOpen, setIsFieldCommandModalOpen] = useState(false);
  const [allDevices, setAllDevices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [selectedDevices, setSelectedDevices] = useState({});
  // Used for Shift+click range selection in the devices table.
  const lastDeviceClickedIdRef = useRef(null);
  const [isDeviceInfoModalOpen, setIsDeviceInfoModalOpen] = useState(false);
  const [deviceInfoModalTitle, setDeviceInfoModalTitle] = useState("");
  const [deviceInfoModalDeviceId, setDeviceInfoModalDeviceId] = useState("");
  const [deviceInfoModalRetailerId, setDeviceInfoModalRetailerId] = useState(null);
  const [deviceInfoModalBranchId, setDeviceInfoModalBranchId] = useState(null);
  const [deviceInfoModalJson, setDeviceInfoModalJson] = useState("");
  const [deviceInfoModalTab, setDeviceInfoModalTab] = useState("info");
  const [deviceInfoModalLocationSource, setDeviceInfoModalLocationSource] = useState("mdm");
  const [branchMapByRetailerStore, setBranchMapByRetailerStore] = useState(null);
  const [coordinateMapByRetailerStore, setCoordinateMapByRetailerStore] = useState(null);
  const [isBranchMapDataLoading, setIsBranchMapDataLoading] = useState(true);
  const [branchMapDataError, setBranchMapDataError] = useState("");
  const [refreshingDeviceInfoModalTabs, setRefreshingDeviceInfoModalTabs] = useState({});
  const [commandError, setCommandError] = useState("");
  const [isSendingCommand, setIsSendingCommand] = useState(false);
  const [commandPayloadText, setCommandPayloadText] = useState("");
  const [commandMessageText, setCommandMessageText] = useState("");
  const [commandEndpoint, setCommandEndpoint] = useState("/MDM/Fully/DeviceInfo");
  const [commandProgress, setCommandProgress] = useState({
    total: 0,
    done: 0,
    success: 0,
    failed: 0,
    status: "idle",
  });
  const [commandUpdatedDevices, setCommandUpdatedDevices] = useState(0);
  const [commandSuccessfulDeviceIds, setCommandSuccessfulDeviceIds] = useState([]);
  const [commandFailedDeviceIds, setCommandFailedDeviceIds] = useState([]);
  const [showSuccessfulDeviceIds, setShowSuccessfulDeviceIds] = useState(false);
  const [showFailedDeviceIds, setShowFailedDeviceIds] = useState(false);
  const [openColumnFilter, setOpenColumnFilter] = useState("");
  const [isColumnsPanelOpen, setIsColumnsPanelOpen] = useState(false);
  const [deviceFilters, setDeviceFilters] = useState(createDefaultDeviceFilters);
  const [sortField, setSortField] = useState("agentCheckIn");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRetailers, setExpandedRetailers] = useState({});
  const [expandedBranches, setExpandedBranches] = useState({});
  const [selectedRetailerName, setSelectedRetailerName] = useState("");
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [folders, setFolders] = useState([]);
  const [folderAssignments, setFolderAssignments] = useState({});
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [moveTarget, setMoveTarget] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [foldersError, setFoldersError] = useState("");
  const [isMovingDevices, setIsMovingDevices] = useState(false);
  const [folderActionError, setFolderActionError] = useState("");
  const [isRestartingCache, setIsRestartingCache] = useState(false);
  const [fieldStatusCounts, setFieldStatusCounts] = useState(null);
  const [fieldJourneyStatusByDeviceId, setFieldJourneyStatusByDeviceId] = useState({});
  const [fieldStatusSnapshotsByBranchKey, setFieldStatusSnapshotsByBranchKey] = useState({});
  const [selectedFieldJourneyStatus, setSelectedFieldJourneyStatus] = useState(null);
  const [isRefreshingFieldStatus, setIsRefreshingFieldStatus] = useState(false);
  const [commandOptions, setCommandOptions] = useState([]);
  const [fullySettingsAllowedOptions, setFullySettingsAllowedOptions] = useState([]);
  const [fullySettingsGroupKey, setFullySettingsGroupKey] = useState("");
  const [k10AllowedOptions, setK10AllowedOptions] = useState([]);
  const [k10GroupKey, setK10GroupKey] = useState("");
  const [fullySettingKey, setFullySettingKey] = useState("");
  const [fullySettingValue, setFullySettingValue] = useState("");
  const [fullySettingBooleanValue, setFullySettingBooleanValue] = useState(true);
  const [loadApkUrl, setLoadApkUrl] = useState("");
  const FIELD_TRIGGER_CONFIG_UPDATE_ENDPOINT = "/MDM/Field/TriggerConfigUpdate";
  const FIELD_TRIGGER_MICROMOMENT_ENDPOINT = "/Campaigns/Trigger/Micro";
  const fieldDatabaseUpdateOptions = [
    { label: "Update Database", value: FIELD_UPDATE_DATABASE_ENDPOINT },
    { label: "Update Database No Timestamp", value: FIELD_UPDATE_DATABASE_NO_TIMESTAMP_ENDPOINT },
    { label: "Clear All and Update", value: FIELD_CLEAR_ALL_AND_UPDATE_ENDPOINT },
  ];
  const fieldNcrServerOptions = FIELD_NCR_SERVER_OPTIONS;
  const [selectedNcrServerTarget, setSelectedNcrServerTarget] = useState(
    FIELD_NCR_SERVER_OPTIONS[0]?.value || "MAIN"
  );
  const fieldCommandOptions = [
    { label: "Trigger Config Update", value: FIELD_TRIGGER_CONFIG_UPDATE_ENDPOINT },
    { label: "Trigger Database Update", value: FIELD_UPDATE_DATABASE_ENDPOINT },
    { label: "Change NCR Server", value: FIELD_CHANGE_NCR_SERVER_ENDPOINT },
    { label: "Fetch NCR Status", value: FIELD_FETCH_NCR_STATUS_ENDPOINT },
    { label: "Trigger Micromoment", value: FIELD_TRIGGER_MICROMOMENT_ENDPOINT },
    { label: "Edit Allowed", value: FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT },
    { label: "Release Device From Cradle", value: FIELD_SCAN_AND_GO_RELEASE_DEVICE_ENDPOINT },
    { label: "Release Wall", value: FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT },
    { label: "Multi Swan Song", value: FIELD_MULTI_SWAN_SONG_ENDPOINT },
    { label: "Camera", value: FIELD_TRIGGER_CAMERA_ENDPOINT },
    { label: "Screenshot", value: FIELD_TRIGGER_SCREENSHOT_ENDPOINT },
    { label: "Get Location/Version", value: FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT },
  ];
  const [fieldConfigOptions, setFieldConfigOptions] = useState({
    branding_config: [],
    scan_and_go_config: [],
    device_config: [],
  });
  const [selectedFieldConfigCategory, setSelectedFieldConfigCategory] = useState("device_config");
  const [selectedFieldConfigValue, setSelectedFieldConfigValue] = useState("");
  const [isFetchingFieldConfig, setIsFetchingFieldConfig] = useState(false);
  const [fieldConfigError, setFieldConfigError] = useState("");
  const [micromomentOptions, setMicromomentOptions] = useState([]);
  const [selectedMicromomentId, setSelectedMicromomentId] = useState("");
  const [isFetchingMicromoments, setIsFetchingMicromoments] = useState(false);
  const [micromomentError, setMicromomentError] = useState("");
  const [fieldCommandResponse, setFieldCommandResponse] = useState(null);
  const [triggeredSwanSongDeviceIds, setTriggeredSwanSongDeviceIds] = useState([]);
  const [triggeredCameraDevices, setTriggeredCameraDevices] = useState([]);
  const [triggeredScreenshotDevices, setTriggeredScreenshotDevices] = useState([]);
  const [locationVersionFetchedDeviceIds, setLocationVersionFetchedDeviceIds] = useState([]);
  const [isLocationVersionWindowOpen, setIsLocationVersionWindowOpen] = useState(false);
  const [locationVersionWindowDeviceIds, setLocationVersionWindowDeviceIds] = useState([]);
  const [isRefreshingLocationVersionMdm, setIsRefreshingLocationVersionMdm] = useState(false);
  const [locationVersionMdmRefreshError, setLocationVersionMdmRefreshError] = useState("");
  const [isRefreshingLocationVersionLogs, setIsRefreshingLocationVersionLogs] = useState(false);
  const [locationVersionLogsRefreshError, setLocationVersionLogsRefreshError] = useState("");
  const [selectedScanAndGoWallIds, setSelectedScanAndGoWallIds] = useState([]);
  const [allowedReleaseForScanAndGo, setAllowedReleaseForScanAndGo] = useState(true);
  const [allowedReleaseNotes, setAllowedReleaseNotes] = useState("");
  const [k10CsdkEnabled, setK10CsdkEnabled] = useState(true);
  const [k10CsdkOnStartAppEnabled, setK10CsdkOnStartAppEnabled] = useState(true);
  const [k10CsdkScheduleHour, setK10CsdkScheduleHour] = useState(0);
  const [k10CsdkScheduleMinute, setK10CsdkScheduleMinute] = useState(0);
  const [k10CsdkScheduleEnabled, setK10CsdkScheduleEnabled] = useState(true);
  const [commandScope, setCommandScope] = useState("fully");
  const [deviceActionResults, setDeviceActionResults] = useState({});
  const [activeManagedColumnKeys, setActiveManagedColumnKeys] = useState(
    DEFAULT_ACTIVE_MANAGED_COLUMN_KEYS
  );
  const [canEditDeviceNotes, setCanEditDeviceNotes] = useState(false);
  const [savingNotesDeviceIds, setSavingNotesDeviceIds] = useState({});
  const [isBulkNotesModalOpen, setIsBulkNotesModalOpen] = useState(false);
  const [isSavingBulkNotes, setIsSavingBulkNotes] = useState(false);
  const DEVICE_MODAL_TAB_ENDPOINTS = {
    screenshot: "/MDM/Fully/GetScreenshot",
    device_config: "/MDM/Field/FetchDeviceConfigLog",
    branding_config: "/MDM/Field/FetchBrandingConfigLog",
    scan_and_go_config: "/MDM/Field/FetchScanAndGoConfigLog",
  };
  const DEVICE_MODAL_TAB_ACTION_KEYS = {
    screenshot: "get_screenshot",
    field_screenshot: "field_screenshot",
    device_config: "fetch_device_config_log",
    branding_config: "fetch_branding_config_log",
    scan_and_go_config: "fetch_scan_and_go_config_log",
  };
  const allDevicesRef = useRef(allDevices);
  const columnsPanelRef = useRef(null);
  const hasLoadedColumnPreferencesRef = useRef(false);
  const columnPreferencesBaselineRef = useRef(null);

  const notify = (message, style) => {
    if (style === "success") {
      toast.success(message, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } else {
      toast.error(message, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };
  useEffect(() => {
    allDevicesRef.current = allDevices;
  }, [allDevices]);

  useEffect(() => {
    let cancelled = false;

    const loadColumnPreferences = async () => {
      const token = getToken();
      if (!token) return;

      try {
        const response = await fetchColumnPreferencesRequest(SERVER_URL, token);
        if (cancelled) return;
        await throwIfRequestFailed(response, "Failed to load column preferences");

        const data = await response.json();
        if (cancelled) return;

        const savedKeys = !data?.is_default
          ? sanitizeActiveManagedColumnKeys(data?.active_column_keys)
          : null;
        const resolvedKeys = savedKeys ?? DEFAULT_ACTIVE_MANAGED_COLUMN_KEYS;
        if (savedKeys !== null) {
          setActiveManagedColumnKeys(savedKeys);
        }
        columnPreferencesBaselineRef.current = JSON.stringify(resolvedKeys);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load column preferences:", error);
          columnPreferencesBaselineRef.current = JSON.stringify(DEFAULT_ACTIVE_MANAGED_COLUMN_KEYS);
        }
      } finally {
        if (!cancelled) {
          hasLoadedColumnPreferencesRef.current = true;
        }
      }
    };

    loadColumnPreferences();
    return () => {
      cancelled = true;
    };
  }, [SERVER_URL, navigate]);

  useEffect(() => {
    if (!hasLoadedColumnPreferencesRef.current) return undefined;

    const serializedKeys = JSON.stringify(activeManagedColumnKeys);
    if (serializedKeys === columnPreferencesBaselineRef.current) return undefined;

    const token = getToken();
    if (!token) return undefined;

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await saveColumnPreferencesRequest(
          SERVER_URL,
          token,
          activeManagedColumnKeys
        );
        if (!response.ok) {
          console.error("Failed to save column preferences:", await response.text());
          return;
        }
        columnPreferencesBaselineRef.current = serializedKeys;
      } catch (error) {
        console.error("Failed to save column preferences:", error);
      }
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [SERVER_URL, activeManagedColumnKeys]);

  useEffect(() => {
    let cancelled = false;
    const fetchBranchMapData = async () => {
      setIsBranchMapDataLoading(true);
      setBranchMapDataError("");
      try {
        const [branchMapResponse, coordinateMapResponse] = await Promise.all([
          fetch(`${SERVER_URL}/BranchMap`),
          fetch(`${SERVER_URL}/coordinateMap`),
        ]);
        if (!branchMapResponse.ok || !coordinateMapResponse.ok) {
          throw new Error("Failed to load branch map data");
        }
        const branchMapPayload = await branchMapResponse.json();
        const coordinateMapPayload = await coordinateMapResponse.json();
        if (cancelled) return;
        setBranchMapByRetailerStore(branchMapPayload?.data ?? null);
        setCoordinateMapByRetailerStore(coordinateMapPayload?.data ?? null);
      } catch (error) {
        if (cancelled) return;
        console.error(error);
        setBranchMapDataError(error?.message || "Failed to load branch map data");
        setBranchMapByRetailerStore(null);
        setCoordinateMapByRetailerStore(null);
      } finally {
        if (!cancelled) setIsBranchMapDataLoading(false);
      }
    };
    fetchBranchMapData();
    return () => {
      cancelled = true;
    };
  }, [SERVER_URL]);

  const throwIfRequestFailed = async (response, defaultMessage) => {
    if (response.ok) return;
    if (response.status === 401) {
      navigate("/");
      throw new Error("Unauthorized");
    }
    else if (response.status === 403) {
      notify("Unauthorized", "error");
    }

    const errorText = await response.text();
    throw new Error(errorText || defaultMessage);
  };

  const notifyIfRequestFailed = async (response, defaultMessage) => {
    if (response.ok) return;
    if (response.status === 401) {
      navigate("/");
      throw new Error("Unauthorized");
    }
    else if (response.status === 403) {
      notify("Unauthorized", "error");
    }

    const errorText = await response.text();
    throw new Error(errorText || defaultMessage);
  };

  const fetchDeviceInfoCache = async (token) => {
    const cacheResponse = await fetchDeviceInfoCacheRequest(SERVER_URL, token);

    await throwIfRequestFailed(cacheResponse, "Failed to refresh device cache");

    const cacheData = await cacheResponse.json();

    const { devices = {}, mdm_endpoints = {} } = cacheData || {};
    allDevicesRef.current = devices;
    setAllDevices(devices);
    const normalizedCommandOptions = normalizeCommandOptions(mdm_endpoints);
    const allowedK10 = buildAllowedK10Options(normalizedCommandOptions);
    const collapsedK10 = collapseK10Commands(normalizedCommandOptions, allowedK10);
    setK10AllowedOptions(allowedK10);
    setK10GroupKey(allowedK10[0]?.value || "");
    const allowedFullySettings = collapsedK10.filter((option) =>
      FULLY_SETTINGS_ENDPOINTS.has(option.value)
    );
    setFullySettingsAllowedOptions(allowedFullySettings);
    setFullySettingsGroupKey(allowedFullySettings[0]?.value || "");
    setCommandOptions(collapseFullySettingsCommands(collapsedK10, allowedFullySettings));
    setCanEditDeviceNotes(true);
    setFetchError("");

    return { devices, mdm_endpoints };
  };

  const handleBulkDeviceNotesSave = async (deviceIds, notes) => {
    const normalizedDeviceIds = [
      ...new Set(
        (deviceIds || []).map((deviceId) => String(deviceId || "").trim()).filter(Boolean)
      ),
    ];
    if (!normalizedDeviceIds.length) return;

    const normalizedNotes = notes == null ? "" : String(notes);
    const token = getToken();
    if (!token) {
      notify("Authentication required to save notes", "error");
      return;
    }

    setIsSavingBulkNotes(true);
    try {
      const response = await updateMultipleDeviceNotesRequest(SERVER_URL, token, {
        device_ids: normalizedDeviceIds,
        notes: normalizedNotes,
      });
      await throwIfRequestFailed(response, "Failed to save device notes");
      const { nextDevices } = patchMultipleDeviceNotesInCache(
        allDevicesRef.current,
        normalizedDeviceIds,
        normalizedNotes
      );
      allDevicesRef.current = nextDevices;
      setAllDevices(nextDevices);
      notify(`Updated notes for ${normalizedDeviceIds.length} device(s)`, "success");
    } catch (error) {
      notify(error?.message || "Failed to save device notes", "error");
      throw error;
    } finally {
      setIsSavingBulkNotes(false);
    }
  };

  const handleDeviceNotesSave = async (deviceId, notes, previousNotes) => {
    const normalizedDeviceId = String(deviceId || "").trim();
    if (!normalizedDeviceId) return;

    const normalizedNotes = notes == null ? "" : String(notes);
    const normalizedPrevious = previousNotes == null ? "" : String(previousNotes);
    if (normalizedNotes === normalizedPrevious) return;

    const token = getToken();
    if (!token) {
      notify("Authentication required to save notes", "error");
      return;
    }

    setSavingNotesDeviceIds((prev) => ({ ...prev, [normalizedDeviceId]: true }));
    try {
      const response = await updateDeviceNotesRequest(SERVER_URL, token, {
        device_id: normalizedDeviceId,
        notes: normalizedNotes,
      });
      await throwIfRequestFailed(response, "Failed to save device notes");
      const { nextDevices } = patchDeviceNotesInCache(allDevicesRef.current, normalizedDeviceId, normalizedNotes);
      allDevicesRef.current = nextDevices;
      setAllDevices(nextDevices);
    } catch (error) {
      notify(error?.message || "Failed to save device notes", "error");
      throw error;
    } finally {
      setSavingNotesDeviceIds((prev) => {
        const next = { ...prev };
        delete next[normalizedDeviceId];
        return next;
      });
    }
  };

  const fetchFoldersData = async (token) => {
    setIsFoldersLoading(true);
    const [foldersResponse, assignmentsResponse] = await Promise.all([
      fetchFoldersRequest(SERVER_URL, token),
      fetchFolderAssignmentsRequest(SERVER_URL, token),
    ]);

    await throwIfRequestFailed(foldersResponse, "Failed to load folders");
    await throwIfRequestFailed(assignmentsResponse, "Failed to load folder assignments");

    const foldersPayload = await foldersResponse.json();
    const assignmentsPayload = await assignmentsResponse.json();
    setFolders(Array.isArray(foldersPayload?.folders) ? foldersPayload.folders : []);
    setFolderAssignments(
      assignmentsPayload?.assignments && typeof assignmentsPayload.assignments === "object"
        ? assignmentsPayload.assignments
        : {}
    );
    setFoldersError("");
  };

  const refreshFolders = async () => {
    try {
      const token = getToken();
      await fetchFoldersData(token);
    } catch (error) {
      console.error(error);
      setFoldersError(error.message || "Failed to refresh folders");
    } finally {
      setIsFoldersLoading(false);
    }
  };

  const refreshAfterDeviceMove = async () => {
    const token = getToken();
    await fetchDeviceInfoCache(token);
    await refreshFolders();
  };

  const getHomeBranchKeysForDeviceIds = (deviceIds) => {
    const keys = new Set();
    deviceIds.forEach((deviceId) => {
      const path = findDevicePath(allDevices, deviceId);
      if (!path?.retailerName || !path?.branchName) return;
      keys.add(getDeviceHomeBranchKey(path.retailerName, path.branchName));
    });
    return keys;
  };

  const assertSingleHomeBranchForMove = (deviceIds) => {
    const keys = getHomeBranchKeysForDeviceIds(deviceIds);
    if (keys.size === 0) {
      throw new Error("Could not determine the home branch for the selected devices.");
    }
    if (keys.size > 1) {
      throw new Error("Selected devices must belong to the same retailer and branch.");
    }
    return [...keys][0];
  };

  useEffect(() => {
    const token = getToken();
    Promise.allSettled([fetchDeviceInfoCache(token), fetchFoldersData(token)])
      .then((results) => {
        const [devicesResult, foldersResult] = results;
        if (devicesResult.status === "rejected") {
          console.error(devicesResult.reason);
          setFetchError("Failed to load devices");
        }
        if (foldersResult.status === "rejected") {
          console.error(foldersResult.reason);
          setFoldersError("Failed to load folders");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setIsFoldersLoading(false);
      });
  }, []);

  const devices = flattenDevices(allDevices).map((device) => {
    const assignment = folderAssignments?.[device.deviceId];
    const branchKey = assignment?.branch_key;
    if (branchKey && typeof branchKey === "string" && branchKey.includes("::")) {
      const { retailerName: retailerOverride, branchName: branchOverride } = parseBranchKey(branchKey);
      return {
        ...device,
        retailerName: retailerOverride || device.retailerName,
        branchName: branchOverride || device.branchName,
      };
    }
    return device;
  });

  const retailerNames = Object.keys(allDevices || {});
  const activeRetailerName = selectedRetailerName || retailerNames[0] || "";
  const activeBranchName =
    selectedBranchName ||
    (activeRetailerName ? Object.keys(allDevices?.[activeRetailerName] || {})[0] || "" : "");

  const scopedDevices = devices.filter((device) => {
    const inFolder = folderAssignments?.[device.deviceId]?.folder_id;
    const retailerFromTree = parseAllRetailerDevicesFolderId(selectedFolderId);
    const viewBranchKeyFromTree =
      parseAllDevicesFolderId(selectedFolderId) ||
      (selectedFolderId
        ? null
        : activeRetailerName && activeBranchName
          ? getBranchKey(activeRetailerName, activeBranchName)
          : null);

    if (retailerFromTree) {
      return String(device.retailerName || "").trim() === retailerFromTree;
    }
    if (
      selectedFolderId &&
      !parseAllDevicesFolderId(selectedFolderId) &&
      !parseAllRetailerDevicesFolderId(selectedFolderId)
    ) {
      return inFolder === selectedFolderId;
    }
    // Branch "All Devices": every device in the cache for this branch (folder is metadata only).
    if (!viewBranchKeyFromTree) return false;
    return getBranchKey(device.retailerName, device.branchName) === viewBranchKeyFromTree;
  });

  const totalDevices = scopedDevices.length;

  const rootMountedFolders = getRootMountedFolders(folders);
  const getRetailerFolders = (retailerName) => getRetailerMountedFolders(folders, retailerName);
  const getBranchFolders = (branchKey) => getBranchMountedFolders(folders, branchKey);
  const getNestedFolders = (parentId) => getChildFolders(folders, parentId);
  const foldersById = buildFoldersById(folders);
  const getResolvedFolderLabel = (folderId) => getFolderFullLabel(folderId, foldersById);
  const getDuplicateFolderPath = (deviceId) =>
    getDeviceDuplicateFolderPath(deviceId, folderAssignments, foldersById);
  const getCurrentFolderName = (deviceId) =>
    getDeviceCurrentFolderName(deviceId, folderAssignments, foldersById);
  const getCurrentFolderFilterValue = (deviceId) =>
    getDeviceCurrentFolderFilterValue(deviceId, folderAssignments, foldersById);
  const folderOptions = buildFolderOptions(folders, foldersById);

  const currentFolderFilterOptions = useMemo(() => {
    const names = new Set();
    let hasUnassigned = false;
    scopedDevices.forEach((device) => {
      const name = getDeviceCurrentFolderName(device.deviceId, folderAssignments, foldersById);
      if (name) {
        names.add(name);
      } else {
        hasUnassigned = true;
      }
    });
    const options = [...names]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
    if (hasUnassigned) {
      options.unshift({ value: UNASSIGNED_FOLDER_FILTER_VALUE, label: "(Unassigned)" });
    }
    return options;
  }, [scopedDevices, folderAssignments, foldersById]);

  const isAllDevicesView = Boolean(
    parseAllRetailerDevicesFolderId(selectedFolderId) ||
    parseAllDevicesFolderId(selectedFolderId) ||
    !selectedFolderId
  );
  const isRetailerLevelFullyView = Boolean(parseAllRetailerDevicesFolderId(selectedFolderId));

  const resolvedBranchKeyForScanAndGoWall = (() => {
    if (isRetailerLevelFullyView) return null;
    const fromVirtual = parseAllDevicesFolderId(selectedFolderId);
    if (fromVirtual) return String(fromVirtual).trim() || null;
    const fromFolderMount = getBranchMountKeyFromFolderId(selectedFolderId, foldersById);
    if (fromFolderMount) return fromFolderMount;
    if (scopedDevices.length > 0) {
      const d = scopedDevices[0];
      return getBranchKey(d.retailerName, d.branchName);
    }
    if (!selectedFolderId && activeRetailerName && activeBranchName) {
      return getBranchKey(activeRetailerName, activeBranchName);
    }
    return null;
  })();
  const fieldStatusBranchKey = resolvedBranchKeyForScanAndGoWall || "";
  const isScanAndGoReleaseWallAllowed = Boolean(resolvedBranchKeyForScanAndGoWall);

  const scanAndGoWallOptions = useMemo(() => {
    if (!resolvedBranchKeyForScanAndGoWall) return [];
    const { retailerName, branchName } = parseBranchKey(resolvedBranchKeyForScanAndGoWall);
    if (!retailerName || !branchName) return [];
    const branchDevices = allDevices?.[retailerName]?.[branchName] || {};
    const seen = new Set();
    const options = [];
    Object.entries(branchDevices).forEach(([, payload]) => {
      const device = { rawPayload: payload || {} };
      const raw = readDevicePayloadField(device, ["wall_id"]);
      const parts = Array.isArray(raw) ? raw : raw !== undefined && raw !== null ? [raw] : [];
      parts.forEach((part) => {
        const s = String(part ?? "").trim();
        if (!s || s.toLowerCase() === "null") return;
        if (seen.has(s)) return;
        seen.add(s);
        options.push({ value: s, label: s });
      });
    });
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [allDevices, resolvedBranchKeyForScanAndGoWall]);

  const branchIdsFromResolvedTree = useMemo(() => {
    if (!resolvedBranchKeyForScanAndGoWall) return { retailerId: null, branchId: null };
    const { retailerName, branchName } = parseBranchKey(resolvedBranchKeyForScanAndGoWall);
    const payloads = Object.values(allDevices?.[retailerName]?.[branchName] || {});
    for (let i = 0; i < payloads.length; i += 1) {
      const p = payloads[i] || {};
      const r = Number(p?.retailer_id ?? p?.retailer?.id);
      const b = Number(p?.branch_id ?? p?.branch?.id);
      if (Number.isFinite(r) && Number.isFinite(b)) return { retailerId: r, branchId: b };
    }
    return { retailerId: null, branchId: null };
  }, [resolvedBranchKeyForScanAndGoWall, allDevices]);

  useEffect(() => {
    if (!isAllDevicesView && sortField === "folderPath") {
      setSortField("agentCheckIn");
      setSortDirection("desc");
    }
  }, [isAllDevicesView, sortField]);

  useEffect(() => {
    if (!retailerNames.length) {
      setSelectedRetailerName("");
      setSelectedBranchName("");
      setExpandedRetailers({});
      setExpandedBranches({});
      return;
    }

    // Don't "reset" the user's current folder/branch selection whenever `allDevices` updates
    // (e.g. after running a device command). Only initialize when nothing is selected.
    if (selectedFolderId) {
      const branchKey = parseAllDevicesFolderId(selectedFolderId);
      if (branchKey) {
        const { retailerName, branchName } = parseBranchKey(branchKey);
        if (retailerName && branchName) {
          setSelectedRetailerName(retailerName);
          setSelectedBranchName(branchName);
          setExpandedRetailers((prev) => ({ ...prev, [retailerName]: true }));
          setExpandedBranches((prev) => ({ ...prev, [branchKey]: true }));
        }
      }
      return;
    }

    const firstRetailer = retailerNames[0];
    const firstBranch = Object.keys(allDevices?.[firstRetailer] || {})[0] || "";

    setSelectedRetailerName(firstRetailer);
    setSelectedBranchName(firstBranch);
    if (firstBranch) {
      const firstBranchKey = getBranchKey(firstRetailer, firstBranch);
      setSelectedFolderId(allDevicesFolderId(firstBranchKey));
      setExpandedBranches((prev) => ({ ...prev, [firstBranchKey]: true }));
    } else {
      setSelectedFolderId("");
    }
    setExpandedRetailers((prev) => ({ ...prev, [firstRetailer]: true }));
  }, [allDevices, selectedFolderId]);

  const toggleRetailer = (retailerName) => {
    const branches = Object.keys(allDevices?.[retailerName] || {});
    setExpandedRetailers((prev) => ({ ...prev, [retailerName]: !prev[retailerName] }));
    setSelectedRetailerName(retailerName);
    const firstBranch = branches[0] || "";
    setSelectedBranchName(firstBranch);
    if (firstBranch) {
      setSelectedFolderId(allDevicesFolderId(getBranchKey(retailerName, firstBranch)));
    } else {
      setSelectedFolderId("");
    }
  };

  const toggleBranch = (retailerName, branchName) => {
    const key = getBranchKey(retailerName, branchName);
    setExpandedBranches((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectBranch = (retailerName, branchName) => {
    setSelectedRetailerName(retailerName);
    setSelectedBranchName(branchName);
    if (branchName) {
      const key = getBranchKey(retailerName, branchName);
      setSelectedFolderId(allDevicesFolderId(key));
      setExpandedBranches((prev) => ({ ...prev, [key]: true }));
    } else {
      setSelectedFolderId("");
    }
    setMoveTarget("");
    setExpandedRetailers((prev) => ({ ...prev, [retailerName]: true }));
  };

  const selectRetailerAllDevices = (retailerName) => {
    setSelectedRetailerName(retailerName);
    setSelectedBranchName("");
    setSelectedFolderId(allRetailerDevicesFolderId(retailerName));
    setMoveTarget("");
    setExpandedRetailers((prev) => ({ ...prev, [retailerName]: true }));
  };

  const SORT_DEFINITIONS = {
    ...createSortDefinitions({
      normalizeTimestampMs,
      cleanSsid,
      getDuplicateFolderPath,
      getDeviceCurrentFolderName: getCurrentFolderName,
      getStatus,
    }),
    ...createOptionalPayloadCacheColumnSortDefinitions({ normalizeTimestampMs }),
  };
  const fullFilterDefinitions = createFullFilterDefinitions({
    normalizeTimestampMs,
    getDeviceCurrentFolderFilterValue: getCurrentFolderFilterValue,
  });
  useEffect(() => {
    if (!SORT_DEFINITIONS[sortField]) {
      setSortField("agentCheckIn");
      setSortDirection("desc");
    }
  }, [sortField]);

  const devicesForTableFilters = selectedFieldJourneyStatus
    ? scopedDevices.filter((device) => {
        const journeyStatus =
          fieldJourneyStatusByDeviceId[String(device.deviceId || "").trim()] || "offline";
        return journeyStatus === selectedFieldJourneyStatus;
      })
    : scopedDevices;

  const filteredDevices = applyDefinedFilters(
    devicesForTableFilters,
    fullFilterDefinitions,
    deviceFilters
  );
  const isSearchFilterActive = String(deviceFilters?.searchText || "").trim() !== "";
  const isDateFilterActive = Boolean(
    String(deviceFilters?.agentCheckInDate?.start || "").trim() ||
    String(deviceFilters?.agentCheckInDate?.end || "").trim()
  );
  const isStatusFilterActive = Array.isArray(deviceFilters?.status) && deviceFilters.status.length > 0;
  const isHasIpFilterActive =
    Array.isArray(deviceFilters?.hasIp) && deviceFilters.hasIp.length > 0;
  const isChargingFilterActive =
    Array.isArray(deviceFilters?.chargingStatus) && deviceFilters.chargingStatus.length > 0;
  const isInKioskFilterActive =
    Array.isArray(deviceFilters?.inKiosk) && deviceFilters.inKiosk.length > 0;
  const isCurrentFolderFilterActive =
    Array.isArray(deviceFilters?.currentFolder) && deviceFilters.currentFolder.length > 0;
  const isBatteryFilterActive =
    String(deviceFilters?.batteryLevel?.min ?? "").trim() !== "0" ||
    String(deviceFilters?.batteryLevel?.max ?? "").trim() !== "100";

  const filteredSortedDevices = [...filteredDevices].sort((a, b) => {
    const definition = SORT_DEFINITIONS[sortField];
    if (!definition) return 0;

    const aValue = definition.getValue(a);
    const bValue = definition.getValue(b);

    if (definition.type === "string") {
      const aString = String(aValue || "").toLowerCase();
      const bString = String(bValue || "").toLowerCase();
      const compare = aString.localeCompare(bString);
      return sortDirection === "asc" ? compare : -compare;
    }

    const aNumber = Number(aValue);
    const bNumber = Number(bValue);
    const aValid = Number.isFinite(aNumber);
    const bValid = Number.isFinite(bNumber);

    if (!aValid && !bValid) return 0;
    if (!aValid) return 1;
    if (!bValid) return -1;

    const compare = aNumber - bNumber;
    return sortDirection === "asc" ? compare : -compare;
  });

  const totalFilteredDevices = filteredSortedDevices.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredDevices / MDM_DEVICES_PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFolderId, deviceFilters, sortField, sortDirection, selectedFieldJourneyStatus]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pageStartIndex = (currentPage - 1) * MDM_DEVICES_PAGE_SIZE;
  const paginatedDevices = filteredSortedDevices.slice(
    pageStartIndex,
    pageStartIndex + MDM_DEVICES_PAGE_SIZE
  );
  const pageEndIndex =
    totalFilteredDevices === 0 ? 0 : Math.min(pageStartIndex + paginatedDevices.length, totalFilteredDevices);

  const selectedDeviceIds = Object.entries(selectedDevices)
    .filter(([, isSelected]) => isSelected)
    .map(([deviceId]) => deviceId);
  const selectedDeviceRows = filteredSortedDevices.filter((device) =>
    Boolean(selectedDevices[device.deviceId])
  );
  const selectedDevicesHomeBranchKey = (() => {
    const keys = getHomeBranchKeysForDeviceIds(selectedDeviceIds);
    if (keys.size === 1) return [...keys][0];
    return null;
  })();
  const moveFolderOptions = selectedDevicesHomeBranchKey
    ? folderOptions.filter((folder) =>
      folderBelongsToBranchKey(folder.id, selectedDevicesHomeBranchKey, foldersById)
    )
    : [];
  const selectedRetailerIds = Array.from(
    new Set(
      selectedDeviceRows
        .map((device) => Number(device?.retailerId))
        .filter((value) => Number.isFinite(value))
    )
  );
  const selectedBranchIds = Array.from(
    new Set(
      selectedDeviceRows
        .map((device) => Number(device?.branchId))
        .filter((value) => Number.isFinite(value))
    )
  );
  const isSingleBranchSelection =
    selectedDeviceRows.length > 0 &&
    selectedRetailerIds.length === 1 &&
    selectedBranchIds.length === 1;
  const isBranchLevelFullyView = Boolean(
    resolvedBranchKeyForScanAndGoWall ||
    (!selectedFolderId && activeRetailerName && activeBranchName)
  );
  const currentBranchRetailerId = branchIdsFromResolvedTree.retailerId;
  const currentBranchStoreId = branchIdsFromResolvedTree.branchId;

  const refreshFieldDeviceStatus = async () => {
    if (
      !Number.isFinite(Number(currentBranchRetailerId)) ||
      !Number.isFinite(Number(currentBranchStoreId))
    ) {
      notify("Missing retailer_id/store for this branch.", "error");
      return;
    }

    setIsRefreshingFieldStatus(true);
    try {
      const token = getToken();
      if (!token) {
        notify("Authentication required to refresh device status", "error");
        return;
      }

      const query = new URLSearchParams({
        retailer: String(currentBranchRetailerId),
        store: String(currentBranchStoreId),
      });
      const response = await fetch(`${SERVER_URL}/carts_last_events?${query}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/");
          return;
        }
        if (response.status === 403) {
          notify("You don't have permission to refresh device status", "error");
          return;
        }
        throw new Error(`Request failed (${response.status})`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error("Invalid device status response");
      }

      const visibleIds = scopedDevices.map((device) => device.deviceId);
      const nextJourneyStatusByDeviceId = buildJourneyStatusByDeviceId(data);
      const nextFieldStatusCounts = countJourneyStatuses(data, visibleIds);
      setFieldJourneyStatusByDeviceId(nextJourneyStatusByDeviceId);
      setFieldStatusCounts(nextFieldStatusCounts);
      if (fieldStatusBranchKey) {
        setFieldStatusSnapshotsByBranchKey((prev) => ({
          ...prev,
          [fieldStatusBranchKey]: {
            counts: nextFieldStatusCounts,
            journeyStatusByDeviceId: nextJourneyStatusByDeviceId,
          },
        }));
      }
      setSelectedFieldJourneyStatus(null);
    } catch (error) {
      notify(error?.message || "Failed to refresh device status", "error");
    } finally {
      setIsRefreshingFieldStatus(false);
    }
  };

  const selectedFilteredCount = filteredSortedDevices.filter((device) =>
    Boolean(selectedDevices[device.deviceId])
  ).length;
  const allDisplayedSelected =
    totalFilteredDevices > 0 && selectedFilteredCount === totalFilteredDevices;
  const isSelectAllIndeterminate =
    selectedFilteredCount > 0 && selectedFilteredCount < totalFilteredDevices;

  useEffect(() => {
    setSelectedDevices({});
    lastDeviceClickedIdRef.current = null;
  }, [selectedFolderId]);

  useEffect(() => {
    const cachedSnapshot = fieldStatusBranchKey
      ? fieldStatusSnapshotsByBranchKey[fieldStatusBranchKey]
      : null;
    setFieldStatusCounts(cachedSnapshot?.counts ?? null);
    setFieldJourneyStatusByDeviceId(cachedSnapshot?.journeyStatusByDeviceId ?? {});
    setSelectedFieldJourneyStatus(null);
  }, [fieldStatusBranchKey, fieldStatusSnapshotsByBranchKey]);

  const handleFieldJourneyStatusFilterToggle = (status) => {
    if (!Object.keys(fieldJourneyStatusByDeviceId).length) return;
    setSelectedFieldJourneyStatus((prev) => (prev === status ? null : status));
  };

  useEffect(() => {
    if (!moveTarget || !selectedDevicesHomeBranchKey) {
      if (moveTarget && selectedDeviceIds.length > 0 && !selectedDevicesHomeBranchKey) {
        setMoveTarget("");
      }
      return;
    }
    if (moveTarget.startsWith("branch::")) {
      const branchKey = moveTarget.replace("branch::", "");
      if (branchKey !== selectedDevicesHomeBranchKey) setMoveTarget("");
      return;
    }
    if (moveTarget.startsWith("folder::")) {
      const folderId = moveTarget.replace("folder::", "");
      if (!folderBelongsToBranchKey(folderId, selectedDevicesHomeBranchKey, foldersById)) {
        setMoveTarget("");
      }
    }
  }, [moveTarget, selectedDevicesHomeBranchKey, selectedDeviceIds.length, foldersById]);

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDevices((prev) => ({
      ...prev,
      [deviceId]: !prev[deviceId],
    }));
  };

  const openDeviceInfoJsonModal = (device) => {
    const title = device?.deviceId || "Unknown Device";
    setDeviceInfoModalTitle(title);
    setDeviceInfoModalDeviceId(String(device?.deviceId || "").trim());
    setDeviceInfoModalRetailerId(device?.retailerId ?? null);
    setDeviceInfoModalBranchId(device?.branchId ?? null);
    setDeviceInfoModalTab("info");
    try {
      setDeviceInfoModalJson(
        device?.deviceInfo ? JSON.stringify(device.deviceInfo, null, 2) : "No device_info found"
      );
    } catch {
      // Fallback in case the payload contains non-serializable values.
      setDeviceInfoModalJson(
        device?.deviceInfo ? String(device.deviceInfo) : "No device_info found"
      );
    }
    setIsDeviceInfoModalOpen(true);
  };

  const closeDeviceInfoModal = () => {
    setIsDeviceInfoModalOpen(false);
    setDeviceInfoModalTab("info");
    setDeviceInfoModalLocationSource("mdm");
    setDeviceInfoModalDeviceId("");
    setDeviceInfoModalRetailerId(null);
    setDeviceInfoModalBranchId(null);
  };

  const handleDeviceInfoModalRefresh = async (tabOverride) => {
    const deviceId = String(deviceInfoModalDeviceId || "").trim();
    const activeTab = typeof tabOverride === "string" ? tabOverride : deviceInfoModalTab || "info";
    if (!deviceId || refreshingDeviceInfoModalTabs[activeTab]) return;
    if (activeTab === "field_screenshot") return;
    const endpoint =
      activeTab === "info"
        ? DEVICE_INFO_NO_STREAM_ENDPOINT
        : activeTab === "location"
          ? deviceInfoModalLocationSource === "logs"
            ? "/MDM/Field/FetchLocationLog"
            : DEVICE_INFO_NO_STREAM_ENDPOINT
          : DEVICE_MODAL_TAB_ENDPOINTS[activeTab] || "/MDM/Fully/GetScreenshot";

    try {
      setRefreshingDeviceInfoModalTabs((prev) => ({
        ...prev,
        [activeTab]: true,
      }));
      const token = getToken();
      const displayedDevice =
        devices.find((device) => String(device?.deviceId || "").trim() === deviceId) || null;
      const resolvedPath = findDevicePath(allDevicesRef.current, deviceId);
      const cachedPayload = resolvedPath
        ? allDevicesRef.current?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[deviceId]
        : null;
      const resolvedRetailerId =
        displayedDevice?.retailerId ??
        deviceInfoModalRetailerId ??
        cachedPayload?.retailer_id ??
        cachedPayload?.retailer?.id ??
        (Number.isFinite(Number(currentBranchRetailerId)) ? Number(currentBranchRetailerId) : null);
      const resolvedBranchId =
        displayedDevice?.branchId ??
        deviceInfoModalBranchId ??
        cachedPayload?.branch_id ??
        cachedPayload?.branch?.id ??
        (Number.isFinite(Number(currentBranchStoreId)) ? Number(currentBranchStoreId) : null);
      const requestBody = {
        device_id: deviceId,
        retailer_id: resolvedRetailerId,
        branch_id: resolvedBranchId,
      };
      const isFieldConfigLogEndpoint =
        endpoint === "/MDM/Field/FetchDeviceConfigLog" ||
        endpoint === "/MDM/Field/FetchBrandingConfigLog" ||
        endpoint === "/MDM/Field/FetchScanAndGoConfigLog" ||
        endpoint === "/MDM/Field/FetchLocationLog";
      if (
        isFieldConfigLogEndpoint &&
        (!requestBody.device_id ||
          requestBody.retailer_id === null ||
          requestBody.retailer_id === undefined ||
          requestBody.branch_id === null ||
          requestBody.branch_id === undefined)
      ) {
        throw new Error("Device ID, retailer_id and branch_id are required for this tab.");
      }
      if (
        endpoint === DEVICE_INFO_NO_STREAM_ENDPOINT ||
        endpoint.startsWith("/MDM/Fully/")
      ) {
        requestBody.devices = [deviceId];
      }
      const response = await sendDeviceCommandRequest(SERVER_URL, endpoint, token, requestBody);

      await notifyIfRequestFailed(response, "Failed to refresh data");

      const contentType = response.headers.get("content-type") || "";
      let payload = null;
      let normalizedData = null;

      const trySetNormalizedData = (candidate) => {
        if (normalizedData || !candidate) return;
        const normalizedCandidate =
          candidate &&
            typeof candidate === "object" &&
            String(candidate?.response || "").toLowerCase() === "success" &&
            Object.prototype.hasOwnProperty.call(candidate, "data")
            ? {
              ...candidate,
              status: candidate?.status || "ok",
            }
            : candidate;
        const normalized = normalizeDeviceResult(normalizedCandidate);
        if (String(normalized?.deviceId || "").trim() === deviceId) {
          normalizedData = normalized.normalizedResult;
        }
      };

      if (contentType.includes("text/event-stream")) {
        if (!response.body) throw new Error("Failed to read response stream.");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() || "";

          chunks.forEach((rawBlock) => {
            const parsedEvent = parseSseEventBlock(rawBlock);
            if (!parsedEvent || parsedEvent.event !== "device_result") return;
            if (typeof parsedEvent.data === "object" && parsedEvent.data !== null) {
              trySetNormalizedData(parsedEvent.data);
            }
          });
        }

        const parsedRemainder = parseSseEventBlock(buffer);
        if (
          parsedRemainder &&
          parsedRemainder.event === "device_result" &&
          typeof parsedRemainder.data === "object" &&
          parsedRemainder.data !== null
        ) {
          trySetNormalizedData(parsedRemainder.data);
        }
      } else {
        payload = contentType.includes("application/json")
          ? await response.json()
          : await response.text();

        if (Array.isArray(payload)) {
          payload.forEach(trySetNormalizedData);
        } else if (payload && typeof payload === "object") {
          if (Array.isArray(payload.results)) {
            payload.results.forEach(trySetNormalizedData);
          } else if (payload.devices && typeof payload.devices === "object") {
            Object.entries(payload.devices).forEach(([resultDeviceId, value]) => {
              trySetNormalizedData({ [resultDeviceId]: value });
            });
          } else if (endpoint === DEVICE_INFO_NO_STREAM_ENDPOINT) {
            Object.entries(payload).forEach(([resultDeviceId, value]) => {
              if (resultDeviceId === "message" || resultDeviceId === "response") return;
              trySetNormalizedData({ [resultDeviceId]: value });
            });
          } else {
            trySetNormalizedData(payload);
          }
        }
      }

      if (activeTab === "info" || (activeTab === "location" && deviceInfoModalLocationSource === "mdm")) {
        if (normalizedData?.status === "ok" || normalizedData?.status === "success") {
          const merged = mergeDeviceResultsIntoCache(allDevicesRef.current, [normalizedData]);
          if (merged.updatedCount > 0) {
            allDevicesRef.current = merged.nextDevices;
            setAllDevices(merged.nextDevices);
          }
        }

        await fetchDeviceInfoCache(token).catch((err) => {
          console.error(err);
        });

        const resolvedPath = findDevicePath(allDevicesRef.current, deviceId);
        const cachedDeviceInfo = resolvedPath
          ? allDevicesRef.current?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[
            deviceId
          ]?.device_info || null
          : null;
        const refreshedDeviceInfo =
          cachedDeviceInfo ||
          normalizedData?.device_info ||
          normalizedData?.deviceInfo ||
          normalizedData?.data?.device_info ||
          normalizedData?.data ||
          payload;
        try {
          setDeviceInfoModalJson(
            refreshedDeviceInfo
              ? JSON.stringify(refreshedDeviceInfo, null, 2)
              : "No device_info found"
          );
        } catch {
          setDeviceInfoModalJson(String(refreshedDeviceInfo || "No device_info found"));
        }
        return;
      }

      const actionKey = getActionKeyForEndpoint(endpoint);
      upsertDeviceActionResult(deviceId, actionKey, {
        endpoint,
        action: actionKey,
        status: normalizedData?.status || "ok",
        data: normalizedData?.data ?? normalizedData ?? payload,
        raw_result: normalizedData ?? payload,
        updated_at:
          normalizedData?.updated_at ||
          normalizedData?.data?.updated_at ||
          new Date().toISOString(),
      });
    } catch (error) {
      console.error(error);
      window.alert(error.message || "Failed to refresh data");
    } finally {
      setRefreshingDeviceInfoModalTabs((prev) => ({
        ...prev,
        [activeTab]: false,
      }));
    }
  };

  const deviceInfoModalParsed = (() => {
    if (!deviceInfoModalJson || deviceInfoModalJson === "No device_info found") return null;
    try {
      return JSON.parse(deviceInfoModalJson);
    } catch {
      return null;
    }
  })();
  const deviceInfoModalCachedLocationLog = (() => {
    const resolvedPath = findDevicePath(allDevicesRef.current, deviceInfoModalDeviceId);
    if (!resolvedPath) return null;
    return (
      allDevicesRef.current?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[
        deviceInfoModalDeviceId
      ]?.location_log || null
    );
  })();
  const deviceInfoModalLocationLogResult =
    deviceActionResults?.[deviceInfoModalDeviceId]?.fetch_location_log ||
    (deviceInfoModalCachedLocationLog
      ? {
        status: "ok",
        data: deviceInfoModalCachedLocationLog,
        updated_at: deviceInfoModalCachedLocationLog?.updated_at,
      }
      : null);
  const deviceInfoModalLocationHistory = (() => {
    const resolvedPath = findDevicePath(allDevices, deviceInfoModalDeviceId);
    if (!resolvedPath) return [];
    return (
      allDevices?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[
        deviceInfoModalDeviceId
      ]?.location_history || []
    );
  })();
  const deviceInfoModalLocationParsed =
    deviceInfoModalLocationSource === "logs"
      ? extractLocationFromLogData(deviceInfoModalLocationLogResult)
      : extractLocationFromDeviceInfo(deviceInfoModalParsed);
  const deviceInfoModalStoreCoordinates = resolveStoreMapEntry(
    coordinateMapByRetailerStore,
    deviceInfoModalRetailerId,
    deviceInfoModalBranchId
  );
  const deviceInfoModalBranchMapData = resolveStoreMapEntry(
    branchMapByRetailerStore,
    deviceInfoModalRetailerId,
    deviceInfoModalBranchId
  );

  const locationVersionWindowDeviceRows = useMemo(() => {
    const idSet = new Set(
      locationVersionWindowDeviceIds.map((id) => String(id).trim()).filter(Boolean)
    );
    if (!idSet.size) return [];
    const flatDevices = flattenDevices(allDevices);
    return [...idSet].map((deviceId) => {
      const device =
        flatDevices.find((entry) => String(entry?.deviceId || "").trim() === deviceId);
      return device || { deviceId, retailerName: null, branchName: null, deviceInfo: {}, rawPayload: {} };
    });
  }, [allDevices, locationVersionWindowDeviceIds]);

  const locationVersionWindowBranchLabel = (() => {
    if (activeRetailerName && activeBranchName) {
      return `${activeRetailerName} / ${activeBranchName}`;
    }
    const first = locationVersionWindowDeviceRows[0];
    if (first?.retailerName && first?.branchName) {
      return `${first.retailerName} / ${first.branchName}`;
    }
    return "";
  })();

  const locationVersionWindowStoreCoordinates = resolveStoreMapEntry(
    coordinateMapByRetailerStore,
    currentBranchRetailerId,
    currentBranchStoreId
  );
  const locationVersionWindowBranchMapData = resolveStoreMapEntry(
    branchMapByRetailerStore,
    currentBranchRetailerId,
    currentBranchStoreId
  );

  const deviceInfoModalScreenshotResult =
    deviceActionResults?.[deviceInfoModalDeviceId]?.get_screenshot || null;
  const deviceInfoModalCachedFieldScreenshot = (() => {
    const resolvedPath = findDevicePath(allDevicesRef.current, deviceInfoModalDeviceId);
    if (!resolvedPath) return null;
    return (
      allDevicesRef.current?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[
        deviceInfoModalDeviceId
      ]?.field_screenshot || null
    );
  })();
  const deviceInfoModalFieldScreenshotResult =
    deviceActionResults?.[deviceInfoModalDeviceId]?.field_screenshot ||
    (deviceInfoModalCachedFieldScreenshot
      ? {
          status: "ok",
          data: deviceInfoModalCachedFieldScreenshot,
          updated_at: deviceInfoModalCachedFieldScreenshot?.updated_at,
        }
      : null);
  const deviceInfoModalActiveLogResult = (() => {
    const actionKey = DEVICE_MODAL_TAB_ACTION_KEYS[deviceInfoModalTab];
    if (
      !actionKey ||
      deviceInfoModalTab === "screenshot" ||
      deviceInfoModalTab === "field_screenshot" ||
      deviceInfoModalTab === "info" ||
      deviceInfoModalTab === "location"
    )
      return null;
    return deviceActionResults?.[deviceInfoModalDeviceId]?.[actionKey] || null;
  })();
  const deviceInfoModalScreenshotUrl = getScreenshotUrlFromResult(deviceInfoModalScreenshotResult);
  const deviceInfoModalFieldScreenshotUrl = getScreenshotUrlFromResult(
    deviceInfoModalFieldScreenshotResult
  );
  const deviceInfoModalLogJson = (() => {
    if (!deviceInfoModalActiveLogResult) return "";
    const logPayload = {
      response:
        deviceInfoModalActiveLogResult?.raw_result?.response ||
        (String(deviceInfoModalActiveLogResult?.status || "").toLowerCase() === "ok"
          ? "success"
          : deviceInfoModalActiveLogResult?.status || ""),
      data: deviceInfoModalActiveLogResult?.data ?? null,
      device_id:
        deviceInfoModalActiveLogResult?.raw_result?.device_id || deviceInfoModalDeviceId || "",
      updated_at:
        deviceInfoModalActiveLogResult?.raw_result?.updated_at ||
        deviceInfoModalActiveLogResult?.updated_at ||
        "",
    };
    try {
      return JSON.stringify(logPayload, null, 2);
    } catch {
      return String(logPayload);
    }
  })();
  const deviceInfoModalLogParsed = (() => {
    if (!deviceInfoModalLogJson) return null;
    try {
      return JSON.parse(deviceInfoModalLogJson);
    } catch {
      return null;
    }
  })();
  const isRefreshingActiveDeviceInfoTab = Boolean(
    refreshingDeviceInfoModalTabs[deviceInfoModalTab || "info"]
  );
  const handleDeviceInfoModalTabChange = (nextTab) => {
    setDeviceInfoModalTab(nextTab);
  };

  const selectDeviceRange = (anchorDeviceId, targetDeviceId) => {
    const anchorIndex = filteredSortedDevices.findIndex((d) => d.deviceId === anchorDeviceId);
    const targetIndex = filteredSortedDevices.findIndex((d) => d.deviceId === targetDeviceId);
    if (anchorIndex === -1 || targetIndex === -1) return false;

    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);

    setSelectedDevices((prev) => {
      const next = { ...prev };
      for (let i = start; i <= end; i += 1) {
        next[filteredSortedDevices[i].deviceId] = true;
      }
      return next;
    });

    return true;
  };

  const handleDeviceSelectionClick = (e, deviceId) => {
    const shiftPressed = Boolean(e?.shiftKey);
    const anchorId = lastDeviceClickedIdRef.current;

    if (shiftPressed && anchorId) {
      const didRangeSelect = selectDeviceRange(anchorId, deviceId);
      lastDeviceClickedIdRef.current = deviceId;
      if (didRangeSelect) return;
      // Fall back to simple toggle if we couldn't compute the range.
    }

    lastDeviceClickedIdRef.current = deviceId;
    toggleDeviceSelection(deviceId);
  };

  const toggleAllDisplayedSelection = () => {
    const shouldSelectAll = !allDisplayedSelected;
    setSelectedDevices((prev) => {
      const next = { ...prev };
      filteredSortedDevices.forEach((device) => {
        next[device.deviceId] = shouldSelectAll;
      });
      return next;
    });
  };

  const setRangeFilterValue = (filterId, key, value) => {
    setDeviceFilters((prev) => ({
      ...prev,
      [filterId]: {
        ...(prev[filterId] || {}),
        [key]: value,
      },
    }));
  };

  const setDateRangeFilterValue = (filterId, key, value) => {
    setDeviceFilters((prev) => ({
      ...prev,
      [filterId]: {
        ...(prev[filterId] || {}),
        [key]: value,
      },
    }));
  };

  const setSearchFilterValue = (value) => {
    setDeviceFilters((prev) => ({
      ...prev,
      searchText: value,
    }));
  };

  const setDropdownFilterValue = (filterId, values) => {
    setDeviceFilters((prev) => ({
      ...prev,
      [filterId]: values,
    }));
  };

  const toggleColumnFilter = (filterId) => {
    setOpenColumnFilter((prev) => (prev === filterId ? "" : filterId));
  };

  const closeColumnFilters = () => {
    setOpenColumnFilter("");
  };

  useEffect(() => {
    if (!isColumnsPanelOpen) return undefined;

    const handlePointerDownOutside = (event) => {
      if (columnsPanelRef.current?.contains(event.target)) return;
      setIsColumnsPanelOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside);
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, [isColumnsPanelOpen]);

  const handleColumnSortToggle = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    const fieldType = SORT_DEFINITIONS[field]?.type;
    setSortField(field);
    setSortDirection(fieldType === "string" ? "asc" : "desc");
  };

  const exportCurrentViewToExcel = () => {
    exportMdmCurrentViewToExcel({
      displayedDevices: filteredSortedDevices,
      activeManagedColumnKeys,
      isAllDevicesView,
      activeOptionalTimeColumns,
      activeOptionalPayloadCacheColumns,
      deviceActionResults,
      formatTime,
      cleanSsid,
      getStatus,
      getDuplicateFolderPath,
      getCurrentFolderName,
    });
  };

  const commandPayload = {
    devices: selectedDeviceIds,
    sound_params: {
      url: "https://storage.googleapis.com/catch_assets/MDM/sounds/kukuriku.mp3",
      loop: false,
      stream: 2,
      level: 100,
    },
  };
  const textCommandOptions = [
    // {
    //   label: "Overlay Message",
    //   value: "/MDM/Fully/OverlayMessage",
    //   payloadKey: "text",
    // },
  ];

  const visibleFieldCommandOptions = fieldCommandOptions.filter((option) => {
    if (option.value === FIELD_TRIGGER_MICROMOMENT_ENDPOINT && !isBranchLevelFullyView) return false;
    if (option.value === FIELD_UPDATE_DATABASE_ENDPOINT && !isBranchLevelFullyView) return false;
    if (option.value === FIELD_CHANGE_NCR_SERVER_ENDPOINT && !isBranchLevelFullyView) return false;
    if (option.value === FIELD_FETCH_NCR_STATUS_ENDPOINT && !isBranchLevelFullyView) return false;
    if (option.value === FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT && !isBranchLevelFullyView) {
      return false;
    }
    if (option.value === FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT && !isScanAndGoReleaseWallAllowed) {
      return false;
    }
    return true;
  });
  const activeCommandOptions =
    commandScope === "field" ? visibleFieldCommandOptions : commandOptions;
  const isFieldTriggerConfigUpdateCommand =
    commandScope === "field" &&
    commandEndpoint === FIELD_TRIGGER_CONFIG_UPDATE_ENDPOINT;
  const isFieldTriggerDatabaseUpdateCommand =
    commandScope === "field" && FIELD_DATABASE_UPDATE_ENDPOINTS.has(commandEndpoint);
  const isFieldChangeNcrServerCommand =
    commandScope === "field" && commandEndpoint === FIELD_CHANGE_NCR_SERVER_ENDPOINT;
  const isFieldFetchNcrStatusCommand =
    commandScope === "field" && commandEndpoint === FIELD_FETCH_NCR_STATUS_ENDPOINT;
  const isFieldTriggerMicromomentCommand =
    commandScope === "field" &&
    commandEndpoint === FIELD_TRIGGER_MICROMOMENT_ENDPOINT;
  const isFieldScanAndGoReleaseWallCommand =
    commandScope === "field" && commandEndpoint === FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT;
  const isFieldScanAndGoAllowedReleaseCommand =
    commandScope === "field" && commandEndpoint === FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT;
  const isAllowedReleaseNotesRequired =
    isFieldScanAndGoAllowedReleaseCommand && !allowedReleaseForScanAndGo;
  const isAllowedReleaseExecuteBlocked =
    isAllowedReleaseNotesRequired && !String(allowedReleaseNotes || "").trim();
  const isFieldMultiSwanSongCommand =
    commandScope === "field" && commandEndpoint === FIELD_MULTI_SWAN_SONG_ENDPOINT;
  const isFieldTriggerCameraCommand =
    commandScope === "field" && commandEndpoint === FIELD_TRIGGER_CAMERA_ENDPOINT;
  const isFieldTriggerScreenshotCommand =
    commandScope === "field" && commandEndpoint === FIELD_TRIGGER_SCREENSHOT_ENDPOINT;
  const isFieldMultiFetchLocationLogCommand =
    commandScope === "field" && commandEndpoint === FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT;
  const isFullySettingsCommand =
    commandScope === "fully" && FULLY_SETTINGS_ENDPOINTS.has(commandEndpoint);
  const isLoadApkFileCommand =
    commandScope === "fully" && commandEndpoint === LOAD_APK_FILE_ENDPOINT;
  const isK10CsdkToggleCommand =
    commandScope === "fully" && K10_CSDK_TOGGLE_ENDPOINTS.has(commandEndpoint);
  const isK10CsdkScheduleCommand =
    commandScope === "fully" && K10_CSDK_SCHEDULE_ENDPOINTS.has(commandEndpoint);
  const isK10ShutdownCommand =
    commandScope === "fully" && commandEndpoint === K10_LENOVO_SHUTDOWN_ENDPOINT;
  const isK10Command = commandScope === "fully" && K10_COMMAND_ENDPOINTS.has(commandEndpoint);
  const visibleK10Options = k10AllowedOptions;
  const visibleFullySettingsOptions = fullySettingsOptions.filter((option) =>
    fullySettingsAllowedOptions.some((allowed) => allowed.value === option.value)
  );
  const commandBroadcastTargetLabel = (() => {
    if (commandEndpoint === FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT) return "wall(s)";
    if (FIELD_SCAN_AND_GO_DEVICE_IDS_ENDPOINTS.has(commandEndpoint)) return "device ID(s)";
    if (commandEndpoint === FIELD_TRIGGER_MICROMOMENT_ENDPOINT) return "device ID(s)";
    if (FIELD_DATABASE_UPDATE_ENDPOINTS.has(commandEndpoint)) return "device ID(s)";
    if (commandEndpoint === FIELD_CHANGE_NCR_SERVER_ENDPOINT) return "device ID(s)";
    if (commandEndpoint === FIELD_FETCH_NCR_STATUS_ENDPOINT) return "device ID(s)";
    if (commandEndpoint === FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT) return "device ID(s)";
    if (commandEndpoint === FIELD_MULTI_SWAN_SONG_ENDPOINT) return "tablet(s)";
    if (commandEndpoint === FIELD_TRIGGER_CAMERA_ENDPOINT) return "tablet(s)";
    if (commandEndpoint === FIELD_TRIGGER_SCREENSHOT_ENDPOINT) return "tablet(s)";
    return "devices";
  })();
  const fieldConfigCategoryOptions = [
    { value: "device_config", label: "Device Config" },
    { value: "scan_and_go_config", label: "Scan & Go Config" },
    { value: "branding_config", label: "Branding Config" },
  ];
  const selectedFieldConfigValues =
    fieldConfigOptions?.[selectedFieldConfigCategory] &&
      Array.isArray(fieldConfigOptions[selectedFieldConfigCategory])
      ? fieldConfigOptions[selectedFieldConfigCategory]
      : [];
  const fetchMicromomentOptionsForBranch = async (retailerId, storeId) => {
    if (!Number.isFinite(Number(retailerId)) || !Number.isFinite(Number(storeId))) {
      setMicromomentOptions([]);
      setSelectedMicromomentId("");
      setMicromomentError("Missing retailer_id/store for this branch.");
      return;
    }
    try {
      setIsFetchingMicromoments(true);
      setMicromomentError("");
      const token = getToken();
      const response = await fetchMicromomentsRequest(SERVER_URL, token, retailerId, storeId);
      await throwIfRequestFailed(response, "Failed to fetch micromoments");
      const payload = await response.json();
      const normalizedOptions = Array.isArray(payload)
        ? payload
          .map((item) => ({
            label: String(item?.label || "").trim(),
            value: String(item?.value || "").trim(),
          }))
          .filter((item) => item.label && item.value)
        : [];
      setMicromomentOptions(normalizedOptions);
      setSelectedMicromomentId((prev) => {
        if (normalizedOptions.some((opt) => opt.value === prev)) return prev;
        return normalizedOptions[0]?.value || "";
      });
      if (normalizedOptions.length === 0) {
        setMicromomentError("No micromoments found for this branch.");
      }
    } catch (error) {
      console.error(error);
      setMicromomentOptions([]);
      setSelectedMicromomentId("");
      setMicromomentError(error?.message || "Failed to fetch micromoments");
    } finally {
      setIsFetchingMicromoments(false);
    }
  };

  const upsertDeviceActionResult = (deviceId, actionKey, resultRecord) => {
    const normalizedDeviceId = String(deviceId || "").trim();
    if (!normalizedDeviceId) return;
    const normalizedActionKey = normalizeActionKey(actionKey) || "unknown_action";
    setDeviceActionResults((prev) => ({
      ...prev,
      [normalizedDeviceId]: {
        ...(prev[normalizedDeviceId] || {}),
        [normalizedActionKey]: {
          ...(prev?.[normalizedDeviceId]?.[normalizedActionKey] || {}),
          ...resultRecord,
        },
      },
    }));
  };

  const parsedPayloadDeviceCount = (() => {
    try {
      const parsed = JSON.parse(commandPayloadText || "{}");
      if (Array.isArray(parsed?.wall_id)) return parsed.wall_id.length;
      if (Array.isArray(parsed?.devices)) return parsed.devices.length;
      if (Array.isArray(parsed?.device_ids)) return parsed.device_ids.length;
      if (Array.isArray(parsed?.tablets)) return parsed.tablets.length;
      if (Array.isArray(parsed?.serial_numbers)) return parsed.serial_numbers.length;
      return selectedDeviceIds.length;
    } catch {
      return selectedDeviceIds.length;
    }
  })();

  const devicesById = new Map(devices.map((device) => [device.deviceId, device]));
  const commandTargetDevices = selectedDeviceIds.map((deviceId) => {
    const device = devicesById.get(deviceId);
    if (!device) {
      return { deviceId, model: null, locationLabel: null };
    }
    const model =
      device?.deviceInfo?.deviceModel ||
      device?.deviceInfo?.deviceManufacturer ||
      null;
    const locationLabel = [device.retailerName, device.branchName].filter(Boolean).join(" / ") || null;
    return { deviceId, model, locationLabel };
  });
  const nonK10SelectedDevices = isK10Command
    ? commandTargetDevices.filter((device) => device.model !== K10_LENOVO_DEVICE_MODEL)
    : [];
  const isK10ExecuteBlocked = isK10Command && nonK10SelectedDevices.length > 0;
  const progressTotal =
    commandProgress.total > 0 ? commandProgress.total : parsedPayloadDeviceCount;
  const progressDone = Math.min(commandProgress.done, progressTotal);
  const progressPercent =
    progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0;

  useEffect(() => {
    if (!isModalOpen) return;

    if (commandScope === "field") {
      if (!activeCommandOptions.some((option) => option.value === commandEndpoint)) {
        if (FIELD_DATABASE_UPDATE_ENDPOINTS.has(commandEndpoint)) return;
        setCommandEndpoint(activeCommandOptions[0]?.value || "");
      }
      return;
    }

    if (commandScope === "fully") {
      if (!activeCommandOptions.some((option) => option.value === commandEndpoint)) {
        if (FULLY_SETTINGS_ENDPOINTS.has(commandEndpoint)) return;
        if (K10_COMMAND_ENDPOINTS.has(commandEndpoint)) return;
        setCommandEndpoint(activeCommandOptions[0]?.value || "");
      }
    }
  }, [activeCommandOptions, commandEndpoint, commandScope, isModalOpen]);

  const openSwanSongResultTabs = (deviceIds = triggeredSwanSongDeviceIds) => {
    deviceIds.forEach((deviceId) => {
      window.open(`/SwanSong/${deviceId}`, "_blank");
    });
  };

  const openCameraResultTabs = (devices = triggeredCameraDevices) => {
    devices.forEach(({ deviceId, serialNumber }) => {
      const resolvedDeviceId = String(deviceId || serialNumber || "").trim();
      const resolvedSerialNumber = String(serialNumber || deviceId || "").trim();
      if (!resolvedDeviceId) return;
      window.open(
        `/photos/${encodeURIComponent(resolvedSerialNumber)}/${encodeURIComponent(resolvedSerialNumber)}`,
        "_blank"
      );
    });
  };

  const openLocationVersionWindow = (deviceIds = selectedDeviceIds) => {
    const normalizedIds = Array.isArray(deviceIds)
      ? deviceIds.map((id) => String(id).trim()).filter(Boolean)
      : [];
    if (!normalizedIds.length) return;
    setLocationVersionWindowDeviceIds(normalizedIds);
    setIsLocationVersionWindowOpen(true);
  };

  const closeLocationVersionWindow = () => {
    setIsLocationVersionWindowOpen(false);
    setLocationVersionWindowDeviceIds([]);
    setLocationVersionMdmRefreshError("");
    setLocationVersionLogsRefreshError("");
  };

  const refreshLocationVersionMdmDevices = async (
    deviceIds = locationVersionWindowDeviceIds
  ) => {
    const normalizedIds = Array.isArray(deviceIds)
      ? deviceIds.map((id) => String(id).trim()).filter(Boolean)
      : [];
    if (!normalizedIds.length || isRefreshingLocationVersionMdm) return;

    setIsRefreshingLocationVersionMdm(true);
    setLocationVersionMdmRefreshError("");
    try {
      const result = await refreshMdmDeviceInfoForDevices({
        serverUrl: SERVER_URL,
        deviceIds: normalizedIds,
        getToken,
        throwIfRequestFailed,
        mergeDeviceResultsIntoCache,
        allDevicesRef,
        setAllDevices,
        fetchDeviceInfoCache,
        upsertDeviceActionResult,
      });
      if (result.successCount === 0) {
        setLocationVersionMdmRefreshError("No devices returned updated MDM device info.");
      }
    } catch (error) {
      console.error(error);
      setLocationVersionMdmRefreshError(error.message || "Failed to refresh MDM device info");
    } finally {
      setIsRefreshingLocationVersionMdm(false);
    }
  };

  const refreshLocationVersionLogsDevices = async (
    deviceIds = locationVersionWindowDeviceIds
  ) => {
    const normalizedIds = Array.isArray(deviceIds)
      ? deviceIds.map((id) => String(id).trim()).filter(Boolean)
      : [];
    if (!normalizedIds.length || isRefreshingLocationVersionLogs) return;

    setIsRefreshingLocationVersionLogs(true);
    setLocationVersionLogsRefreshError("");
    try {
      const result = await refreshLogsLocationForDevices({
        serverUrl: SERVER_URL,
        deviceIds: normalizedIds,
        retailerId: currentBranchRetailerId,
        branchId: currentBranchStoreId,
        getToken,
        throwIfRequestFailed,
        allDevicesRef,
        setAllDevices,
        upsertDeviceActionResult,
      });
      if (result.successCount === 0) {
        setLocationVersionLogsRefreshError("No devices returned updated location log data.");
      }
    } catch (error) {
      console.error(error);
      setLocationVersionLogsRefreshError(error.message || "Failed to refresh location logs");
    } finally {
      setIsRefreshingLocationVersionLogs(false);
    }
  };

  const toggleScanAndGoWallSelection = (wallId) => {
    const id = String(wallId || "").trim();
    if (!id) return;
    setSelectedScanAndGoWallIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const runCommand = async () => {
    if (isFieldChangeNcrServerCommand) {
      const targetLabel =
        fieldNcrServerOptions.find((option) => option.value === selectedNcrServerTarget)?.label ||
        selectedNcrServerTarget;
      const deviceCount = selectedDeviceIds.length;
      const confirmed = window.confirm(
        `Change NCR server for ${deviceCount} device${deviceCount === 1 ? "" : "s"}?\n\n${targetLabel}`
      );
      if (!confirmed) return;
    }
    setIsSendingCommand(true);
    setCommandError("");
    setCommandUpdatedDevices(0);
    setCommandSuccessfulDeviceIds([]);
    setCommandFailedDeviceIds([]);
    setShowSuccessfulDeviceIds(false);
    setShowFailedDeviceIds(false);
    setFieldCommandResponse(null);
    try {
      const sharedRunnerContext = {
        serverUrl: SERVER_URL,
        endpoint: commandEndpoint,
        commandPayloadText,
        commandMessageText,
        textCommandOptions,
        getToken,
        throwIfRequestFailed,
        setCommandProgress,
        upsertDeviceActionResult,
        allDevicesRef,
        mergeDeviceResultsIntoCache,
        setAllDevices,
        setCommandUpdatedDevices,
        setCommandSuccessfulDeviceIds,
        setCommandFailedDeviceIds,
        fetchDeviceInfoCache,
      };

      if (commandScope === "field") {
        const fieldCommandResult = await runFieldCommand({
          ...sharedRunnerContext,
          isBranchLevelFullyView,
          isSingleFullyBranchSelection: isSingleBranchSelection,
          fieldTriggerConfigUpdateEndpoint: FIELD_TRIGGER_CONFIG_UPDATE_ENDPOINT,
          fieldTriggerMicromomentEndpoint: FIELD_TRIGGER_MICROMOMENT_ENDPOINT,
          fieldScanAndGoReleaseWallEndpoint: FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT,
          fieldMultiSwanSongEndpoint: FIELD_MULTI_SWAN_SONG_ENDPOINT,
          fieldTriggerCameraEndpoint: FIELD_TRIGGER_CAMERA_ENDPOINT,
          fieldTriggerScreenshotEndpoint: FIELD_TRIGGER_SCREENSHOT_ENDPOINT,
          fieldMultiFetchLocationLogEndpoint: FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT,
          fieldFetchNcrStatusEndpoint: FIELD_FETCH_NCR_STATUS_ENDPOINT,
          isScanAndGoReleaseWallAllowed,
          setFieldCommandResponse,
        });
        if (commandEndpoint === FIELD_TRIGGER_CAMERA_ENDPOINT) {
          const normalizeTriggeredCameraDevices = (entries) =>
            (Array.isArray(entries) ? entries : [])
              .map((item) => {
                if (typeof item === "string") {
                  const id = String(item).trim();
                  return id ? { deviceId: id, serialNumber: id } : null;
                }
                const deviceId = String(item?.device_id || item?.deviceId || item?.serial_number || "").trim();
                const serialNumber = String(item?.serial_number || item?.serialNumber || deviceId).trim();
                return deviceId ? { deviceId, serialNumber } : null;
              })
              .filter(Boolean);

          if (Array.isArray(fieldCommandResult) && fieldCommandResult.length) {
            setTriggeredCameraDevices(normalizeTriggeredCameraDevices(fieldCommandResult));
          } else {
            try {
              const parsed = JSON.parse(commandPayloadText || "{}");
              const serialNumbers = Array.isArray(parsed?.serial_numbers)
                ? parsed.serial_numbers.map((id) => String(id).trim()).filter(Boolean)
                : selectedDeviceIds;
              setTriggeredCameraDevices(normalizeTriggeredCameraDevices(serialNumbers));
            } catch {
              setTriggeredCameraDevices(normalizeTriggeredCameraDevices(selectedDeviceIds));
            }
          }
        }
        if (commandEndpoint === FIELD_TRIGGER_SCREENSHOT_ENDPOINT) {
          const normalizeTriggeredScreenshotDevices = (entries) =>
            (Array.isArray(entries) ? entries : [])
              .map((item) => {
                if (typeof item === "string") {
                  const id = String(item).trim();
                  return id ? { deviceId: id, serialNumber: id, screenshots: [] } : null;
                }
                const deviceId = String(item?.device_id || item?.deviceId || item?.serial_number || "").trim();
                const serialNumber = String(item?.serial_number || item?.serialNumber || deviceId).trim();
                const screenshots = Array.isArray(item?.screenshots) ? item.screenshots : [];
                return deviceId ? { deviceId, serialNumber, screenshots } : null;
              })
              .filter(Boolean);

          const normalizedScreenshotDevices = Array.isArray(fieldCommandResult) && fieldCommandResult.length
            ? normalizeTriggeredScreenshotDevices(fieldCommandResult)
            : (() => {
                try {
                  const parsed = JSON.parse(commandPayloadText || "{}");
                  const serialNumbers = Array.isArray(parsed?.serial_numbers)
                    ? parsed.serial_numbers.map((id) => String(id).trim()).filter(Boolean)
                    : selectedDeviceIds;
                  return normalizeTriggeredScreenshotDevices(serialNumbers);
                } catch {
                  return normalizeTriggeredScreenshotDevices(selectedDeviceIds);
                }
              })();

          setTriggeredScreenshotDevices(normalizedScreenshotDevices);

          normalizedScreenshotDevices.forEach((device) => {
            const fieldScreenshotData = buildFieldScreenshotDataFromScreenshots(device.screenshots);
            if (!fieldScreenshotData) return;
            upsertDeviceActionResult(device.deviceId, "field_screenshot", {
              endpoint: FIELD_TRIGGER_SCREENSHOT_ENDPOINT,
              action: "field_screenshot",
              status: "ok",
              data: fieldScreenshotData,
              updated_at: fieldScreenshotData.updated_at,
            });
          });

          if (Array.isArray(fieldCommandResult) && fieldCommandResult.length) {
            const merged = mergeFieldScreenshotResultsIntoCache(
              allDevicesRef.current,
              fieldCommandResult
            );
            if (merged.updatedCount > 0) {
              allDevicesRef.current = merged.nextDevices;
              setAllDevices(merged.nextDevices);
            }
          }
        }
        if (commandEndpoint === FIELD_MULTI_SWAN_SONG_ENDPOINT) {
          try {
            const parsed = JSON.parse(commandPayloadText || "{}");
            const tablets = Array.isArray(parsed?.tablets)
              ? parsed.tablets.map((id) => String(id).trim()).filter(Boolean)
              : selectedDeviceIds;
            setTriggeredSwanSongDeviceIds(tablets);
          } catch {
            setTriggeredSwanSongDeviceIds(selectedDeviceIds);
          }
        }
        if (commandEndpoint === FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT) {
          try {
            const parsed = JSON.parse(commandPayloadText || "{}");
            const deviceIds = Array.isArray(parsed?.device_ids)
              ? parsed.device_ids.map((id) => String(id).trim()).filter(Boolean)
              : selectedDeviceIds;
            setLocationVersionFetchedDeviceIds(deviceIds);
          } catch {
            setLocationVersionFetchedDeviceIds(selectedDeviceIds);
          }
        }
      } else {
        await runMdmCommand(sharedRunnerContext);
      }
    } catch (error) {
      console.error(error);
      setCommandError(error.message || "Failed to send command");
      setCommandProgress((prev) => ({ ...prev, status: "failed" }));
    } finally {
      setIsSendingCommand(false);
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const createNewFolder = async ({ parentId = null, mountType = null, mountKey = null } = {}) => {
    const name = window.prompt(parentId ? "Enter subfolder name" : "Enter folder name");
    if (!name || !name.trim()) return;
    setFolderActionError("");
    try {
      const token = getToken();
      const body = {
        name: name.trim(),
        parent_id: parentId || null,
      };
      if (!parentId) {
        body.mount_type = mountType || "root";
        body.mount_key = mountKey || null;
      }
      const response = await createFolderRequest(SERVER_URL, token, body);
      await notifyIfRequestFailed(response, "Failed to create folder");
      await refreshFolders();
    } catch (error) {
      console.error(error);
      setFolderActionError(error.message || "Failed to create folder");
    }
  };

  const deleteFolder = async (folderId, folderName) => {
    const id = String(folderId || "").trim();
    if (!id) return;
    const label = folderName || id;
    if (!window.confirm(`Delete folder "${label}"? This cannot be undone.`)) return;
    setFolderActionError("");
    try {
      const token = getToken();
      const response = await deleteFolderRequest(SERVER_URL, token, id);
      await notifyIfRequestFailed(response, "Failed to delete folder");
      setExpandedFolders((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (selectedFolderId === id) {
        if (activeRetailerName && activeBranchName) {
          setSelectedFolderId(allDevicesFolderId(getBranchKey(activeRetailerName, activeBranchName)));
        } else {
          setSelectedFolderId("");
        }
      }
      if (moveTarget === `folder::${id}`) {
        setMoveTarget("");
      }
      await refreshFolders();
    } catch (error) {
      console.error(error);
      setFolderActionError(error.message || "Failed to delete folder");
    }
  };

  const moveSelectedDevicesToFolder = async (targetFolderId) => {
    if (!selectedDeviceIds.length) {
      setFolderActionError("Select at least one device before moving.");
      return;
    }
    setFolderActionError("");
    setIsMovingDevices(true);
    try {
      const homeBranchKey = assertSingleHomeBranchForMove(selectedDeviceIds);
      if (!folderBelongsToBranchKey(targetFolderId, homeBranchKey, foldersById)) {
        throw new Error("Devices cannot be moved to a folder outside their home branch.");
      }
      const token = getToken();
      const response = await moveDevicesRequest(SERVER_URL, token, {
        device_ids: selectedDeviceIds,
        to_folder_id: targetFolderId || null,
        to_branch_key: null,
      });
      await notifyIfRequestFailed(response, "Failed to move devices");
      const payload = await response.json();
      await refreshAfterDeviceMove();
      setSelectedDevices({});
      if (Array.isArray(payload?.skipped) && payload.skipped.length > 0) {
        setFolderActionError(`Moved with skips: ${payload.skipped.join(", ")}`);
      }
    } catch (error) {
      console.error(error);
      setFolderActionError(error.message || "Failed to move devices");
    } finally {
      setIsMovingDevices(false);
    }
  };

  const moveSelectedDevicesToBranchAllDevices = async (branchKey) => {
    if (!selectedDeviceIds.length) {
      setFolderActionError("Select at least one device before moving.");
      return;
    }
    const normalizedBranchKey = String(branchKey || "").trim();
    if (!normalizedBranchKey) {
      setFolderActionError("Please choose a branch All Devices destination.");
      return;
    }
    setFolderActionError("");
    setIsMovingDevices(true);
    try {
      const homeBranchKey = assertSingleHomeBranchForMove(selectedDeviceIds);
      if (normalizedBranchKey !== homeBranchKey) {
        throw new Error("Devices cannot be moved to a different retailer or branch.");
      }
      const token = getToken();
      const response = await moveDevicesRequest(SERVER_URL, token, {
        device_ids: selectedDeviceIds,
        to_folder_id: null,
        to_branch_key: normalizedBranchKey,
      });
      await notifyIfRequestFailed(response, "Failed to move devices");
      const payload = await response.json();
      await refreshAfterDeviceMove();
      setSelectedDevices({});

      const { retailerName, branchName } = parseBranchKey(normalizedBranchKey);
      if (retailerName && branchName) {
        setSelectedRetailerName(retailerName);
        setSelectedBranchName(branchName);
      }
      setSelectedFolderId(allDevicesFolderId(normalizedBranchKey));

      if (Array.isArray(payload?.skipped) && payload.skipped.length > 0) {
        setFolderActionError(`Moved with skips: ${payload.skipped.join(", ")}`);
      }
    } catch (error) {

      setFolderActionError(error.message || "Failed to move devices");
    } finally {
      setIsMovingDevices(false);
    }
  };

  const resetCommandModalState = () => {
    setCommandError("");
    setCommandProgress({
      total: 0,
      done: 0,
      success: 0,
      failed: 0,
      status: "idle",
    });
    setCommandUpdatedDevices(0);
    setCommandSuccessfulDeviceIds([]);
    setCommandFailedDeviceIds([]);
    setShowSuccessfulDeviceIds(false);
    setShowFailedDeviceIds(false);
    setCommandMessageText("");
    setFieldCommandResponse(null);
    setSelectedScanAndGoWallIds([]);
    setAllowedReleaseForScanAndGo(true);
    setAllowedReleaseNotes("");
    setK10CsdkEnabled(true);
    setK10CsdkOnStartAppEnabled(true);
    setK10CsdkScheduleHour(0);
    setK10CsdkScheduleMinute(0);
    setK10CsdkScheduleEnabled(true);
    setFullySettingKey("");
    setFullySettingValue("");
    setFullySettingBooleanValue(true);
    setLoadApkUrl("");
  };

  const openCommandModal = () => {
    resetCommandModalState();
    setCommandScope("fully");
    setCommandEndpoint("/MDM/Fully/DeviceInfo");
    setCommandPayloadText(JSON.stringify(commandPayload, null, 2));
    setIsModalOpen(true);
  };

  const openFieldCommandModal = () => {
    resetCommandModalState();
    setFieldConfigError("");
    setMicromomentError("");
    setMicromomentOptions([]);
    setSelectedMicromomentId("");
    setCommandScope("field");
    setCommandEndpoint(visibleFieldCommandOptions[0]?.value || "");
    setCommandPayloadText(JSON.stringify(commandPayload, null, 2));
    setIsFieldCommandModalOpen(true);
    setIsModalOpen(true);
    setSelectedFieldConfigCategory("device_config");
    setSelectedFieldConfigValue("");
    setFieldConfigOptions({
      branding_config: [],
      scan_and_go_config: [],
      device_config: [],
    });
    setIsFetchingFieldConfig(true);
    const token = getToken();
    fetchFieldConfigRequest(SERVER_URL, token)
      .then(async (response) => {
        await throwIfRequestFailed(response, "Failed to fetch Field config");
        return response.json();
      })
      .then((payload) => {
        const normalizedFieldConfigs = {
          branding_config: normalizeStringOptions(payload?.branding_config),
          scan_and_go_config: normalizeStringOptions(payload?.scan_and_go_config),
          device_config: normalizeStringOptions(payload?.device_config),
        };
        setFieldConfigOptions(normalizedFieldConfigs);
      })
      .catch((error) => {
        console.error(error);
        setFieldConfigError(error?.message || "Failed to fetch Field config");
      })
      .finally(() => {
        setIsFetchingFieldConfig(false);
      });
  };

  useEffect(() => {
    if (!isModalOpen || !isFieldTriggerConfigUpdateCommand) return;
    if (!selectedFieldConfigCategory) {
      setSelectedFieldConfigCategory("device_config");
    }
  }, [isFieldTriggerConfigUpdateCommand, isModalOpen, selectedFieldConfigCategory]);

  useEffect(() => {
    if (!isModalOpen || !isFieldTriggerConfigUpdateCommand) return;
    if (selectedFieldConfigValues.includes(selectedFieldConfigValue)) return;
    setSelectedFieldConfigValue(selectedFieldConfigValues[0] || "");
  }, [
    isFieldTriggerConfigUpdateCommand,
    isModalOpen,
    selectedFieldConfigValue,
    selectedFieldConfigValues,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldTriggerConfigUpdateCommand) return;
    const payloadWithSelectedConfig = {
      ...commandPayload,
      config_category: selectedFieldConfigCategory,
      config_value: selectedFieldConfigValue,
    };
    setCommandPayloadText(JSON.stringify(payloadWithSelectedConfig, null, 2));
  }, [
    isFieldTriggerConfigUpdateCommand,
    isModalOpen,
    selectedFieldConfigCategory,
    selectedFieldConfigValue,
    selectedDeviceIds,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldTriggerDatabaseUpdateCommand) return;
    if (!Number.isFinite(Number(currentBranchRetailerId)) || !Number.isFinite(Number(currentBranchStoreId))) {
      return;
    }
    setCommandPayloadText(
      JSON.stringify(
        {
          device_ids: selectedDeviceIds,
          retailer_id: Number(currentBranchRetailerId),
          branch_id: Number(currentBranchStoreId),
        },
        null,
        2
      )
    );
  }, [
    isFieldTriggerDatabaseUpdateCommand,
    isModalOpen,
    selectedDeviceIds,
    currentBranchRetailerId,
    currentBranchStoreId,
    commandEndpoint,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldChangeNcrServerCommand) return;
    if (!Number.isFinite(Number(currentBranchRetailerId)) || !Number.isFinite(Number(currentBranchStoreId))) {
      return;
    }
    setCommandPayloadText(
      JSON.stringify(
        {
          device_ids: selectedDeviceIds,
          retailer_id: Number(currentBranchRetailerId),
          branch_id: Number(currentBranchStoreId),
          notification_message: selectedNcrServerTarget,
        },
        null,
        2
      )
    );
  }, [
    isFieldChangeNcrServerCommand,
    isModalOpen,
    selectedDeviceIds,
    currentBranchRetailerId,
    currentBranchStoreId,
    selectedNcrServerTarget,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isLoadApkFileCommand) return;
    setCommandPayloadText(
      JSON.stringify(
        {
          url: loadApkUrl.trim(),
          devices: selectedDeviceIds,
        },
        null,
        2
      )
    );
  }, [isLoadApkFileCommand, isModalOpen, loadApkUrl, selectedDeviceIds]);

  useEffect(() => {
    if (!isModalOpen || !isFullySettingsCommand) return;
    const value =
      commandEndpoint === FULLY_SET_BOOLEAN_ENDPOINT
        ? fullySettingBooleanValue
        : fullySettingValue;
    setCommandPayloadText(
      JSON.stringify(
        {
          key: fullySettingKey.trim(),
          value,
          devices: selectedDeviceIds,
        },
        null,
        2
      )
    );
  }, [
    isFullySettingsCommand,
    isModalOpen,
    commandEndpoint,
    fullySettingKey,
    fullySettingValue,
    fullySettingBooleanValue,
    selectedDeviceIds,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldCommandModalOpen) return;
    setIsFieldCommandModalOpen(false);
    if (!isBranchLevelFullyView) return;
    fetchMicromomentOptionsForBranch(currentBranchRetailerId, currentBranchStoreId);
  }, [
    isModalOpen,
    isFieldCommandModalOpen,
    isBranchLevelFullyView,
    currentBranchRetailerId,
    currentBranchStoreId,
  ]);
  useEffect(() => {
    if (!isModalOpen || !isFieldTriggerMicromomentCommand) return;
    const payloadWithMicromoment = {
      device_ids: selectedDeviceIds,
      micromomment_id: selectedMicromomentId,
      retailer: currentBranchRetailerId,
      store: currentBranchStoreId,
    };
    setCommandPayloadText(JSON.stringify(payloadWithMicromoment, null, 2));
  }, [
    isFieldTriggerMicromomentCommand,
    isModalOpen,
    selectedMicromomentId,
    selectedDeviceIds,
    currentBranchRetailerId,
    currentBranchStoreId,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldMultiFetchLocationLogCommand) return;
    if (!Number.isFinite(Number(currentBranchRetailerId)) || !Number.isFinite(Number(currentBranchStoreId))) {
      return;
    }
    setCommandPayloadText(
      JSON.stringify(
        {
          device_ids: selectedDeviceIds,
          retailer_id: Number(currentBranchRetailerId),
          branch_id: Number(currentBranchStoreId),
        },
        null,
        2
      )
    );
  }, [
    isFieldMultiFetchLocationLogCommand,
    isModalOpen,
    selectedDeviceIds,
    currentBranchRetailerId,
    currentBranchStoreId,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldFetchNcrStatusCommand) return;
    if (!Number.isFinite(Number(currentBranchRetailerId)) || !Number.isFinite(Number(currentBranchStoreId))) {
      return;
    }
    setCommandPayloadText(
      JSON.stringify(
        {
          device_ids: selectedDeviceIds,
          retailer_id: Number(currentBranchRetailerId),
          branch_id: Number(currentBranchStoreId),
        },
        null,
        2
      )
    );
  }, [
    isFieldFetchNcrStatusCommand,
    isModalOpen,
    selectedDeviceIds,
    currentBranchRetailerId,
    currentBranchStoreId,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldMultiSwanSongCommand) return;
    if (!String(commandMessageText || "").trim()) {
      setCommandMessageText("Trigger swan song");
    }
  }, [isFieldMultiSwanSongCommand, isModalOpen, commandMessageText]);

  useEffect(() => {
    if (!isModalOpen || !isFieldMultiSwanSongCommand) return;
    setCommandPayloadText(
      JSON.stringify(
        {
          tablets: selectedDeviceIds,
          message: String(commandMessageText || "").trim() || "Trigger swan song",
        },
        null,
        2
      )
    );
  }, [isFieldMultiSwanSongCommand, isModalOpen, selectedDeviceIds, commandMessageText]);

  useEffect(() => {
    if (!isModalOpen || !isFieldTriggerCameraCommand) return;
    if (!Number.isFinite(Number(currentBranchRetailerId)) || !Number.isFinite(Number(currentBranchStoreId))) {
      return;
    }
    setCommandPayloadText(
      JSON.stringify(
        {
          serial_numbers: selectedDeviceIds,
          retailer: Number(currentBranchRetailerId),
          store: Number(currentBranchStoreId),
        },
        null,
        2
      )
    );
  }, [
    isFieldTriggerCameraCommand,
    isModalOpen,
    selectedDeviceIds,
    currentBranchRetailerId,
    currentBranchStoreId,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isFieldTriggerScreenshotCommand) return;
    if (!Number.isFinite(Number(currentBranchRetailerId)) || !Number.isFinite(Number(currentBranchStoreId))) {
      return;
    }
    setCommandPayloadText(
      JSON.stringify(
        {
          serial_numbers: selectedDeviceIds,
          retailer: Number(currentBranchRetailerId),
          store: Number(currentBranchStoreId),
        },
        null,
        2
      )
    );
  }, [
    isFieldTriggerScreenshotCommand,
    isModalOpen,
    selectedDeviceIds,
    currentBranchRetailerId,
    currentBranchStoreId,
  ]);

  useEffect(() => {
    if (!isModalOpen || commandScope !== "field") return;
    if (commandEndpoint === FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT) {
      const trimmedAllowedReleaseNotes = String(allowedReleaseNotes || "").trim();
      const allowedReleasePayload = {
        device_ids: selectedDeviceIds,
        allowed: allowedReleaseForScanAndGo,
      };
      if (trimmedAllowedReleaseNotes) {
        allowedReleasePayload.notes = trimmedAllowedReleaseNotes;
      }
      setCommandPayloadText(JSON.stringify(allowedReleasePayload, null, 2));
      return;
    }
    if (commandEndpoint === FIELD_SCAN_AND_GO_RELEASE_DEVICE_ENDPOINT) {
      setCommandPayloadText(
        JSON.stringify(
          {
            device_ids: selectedDeviceIds,
          },
          null,
          2
        )
      );
    }
  }, [
    isModalOpen,
    commandScope,
    commandEndpoint,
    selectedDeviceIds,
    allowedReleaseForScanAndGo,
    allowedReleaseNotes,
  ]);

  useEffect(() => {
    if (!isModalOpen || commandScope !== "fully") return;
    if (!isK10Command) return;

    if (isK10CsdkToggleCommand) {
      setCommandPayloadText(
        JSON.stringify(
          {
            devices: selectedDeviceIds,
            enabled: k10CsdkEnabled,
            on_start_app_enabled: k10CsdkOnStartAppEnabled,
          },
          null,
          2
        )
      );
      return;
    }

    if (isK10CsdkScheduleCommand) {
      setCommandPayloadText(
        JSON.stringify(
          {
            devices: selectedDeviceIds,
            hour: k10CsdkScheduleHour,
            minute: k10CsdkScheduleMinute,
            enabled: k10CsdkScheduleEnabled,
            on_start_app_enabled: k10CsdkOnStartAppEnabled,
          },
          null,
          2
        )
      );
      return;
    }

    if (isK10ShutdownCommand) {
      setCommandPayloadText(
        JSON.stringify(
          {
            devices: selectedDeviceIds,
          },
          null,
          2
        )
      );
    }
  }, [
    isModalOpen,
    commandScope,
    commandEndpoint,
    isK10Command,
    isK10CsdkToggleCommand,
    isK10CsdkScheduleCommand,
    isK10ShutdownCommand,
    selectedDeviceIds,
    k10CsdkEnabled,
    k10CsdkOnStartAppEnabled,
    k10CsdkScheduleHour,
    k10CsdkScheduleMinute,
    k10CsdkScheduleEnabled,
  ]);

  useEffect(() => {
    if (!isModalOpen || commandScope !== "field") return;
    if (commandEndpoint !== FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT) return;
    if (!isScanAndGoReleaseWallAllowed) return;
    if (!Number.isFinite(Number(currentBranchRetailerId)) || !Number.isFinite(Number(currentBranchStoreId))) {
      return;
    }
    setCommandPayloadText(
      JSON.stringify(
        {
          retailer_id: Number(currentBranchRetailerId),
          branch_id: Number(currentBranchStoreId),
          wall_id: selectedScanAndGoWallIds,
        },
        null,
        2
      )
    );
  }, [
    isModalOpen,
    commandScope,
    commandEndpoint,
    isScanAndGoReleaseWallAllowed,
    currentBranchRetailerId,
    currentBranchStoreId,
    selectedScanAndGoWallIds,
  ]);

  useEffect(() => {
    if (!isModalOpen || commandScope !== "field") return;
    if (commandEndpoint !== FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT) return;
    setSelectedScanAndGoWallIds((prev) => {
      const kept = prev.filter((id) => scanAndGoWallOptions.some((o) => o.value === id));
      if (kept.length > 0) return kept;
      return scanAndGoWallOptions.map((o) => o.value);
    });
  }, [isModalOpen, commandScope, commandEndpoint, scanAndGoWallOptions]);

  const restartFullyCache = async () => {
    setFolderActionError("");
    setIsRestartingCache(true);
    try {
      const token = getToken();
      const response = await restartFullyCacheRequest(SERVER_URL, token);
      await throwIfRequestFailed(response, "Failed to restart cache");
      await fetchDeviceInfoCache(token);
      notify("Restart Cache command sent successfully", "success");
    } catch (error) {
      console.error(error);
      setFolderActionError(error.message || "Failed to restart cache");
    } finally {
      setIsRestartingCache(false);
    }
  };

  const handleMoveSelectedDevices = () => {
    if (!moveTarget) {
      setFolderActionError("Choose a destination first.");
      return;
    }
    if (moveTarget.startsWith("folder::")) {
      const folderId = moveTarget.replace("folder::", "");
      moveSelectedDevicesToFolder(folderId);
      return;
    }
    if (moveTarget.startsWith("branch::")) {
      const branchKey = moveTarget.replace("branch::", "");
      moveSelectedDevicesToBranchAllDevices(branchKey);
    }
  };
  const activeOptionalTimeColumns = OPTIONAL_TIME_COLUMNS.filter((column) =>
    activeManagedColumnKeys.includes(column.key)
  );
  const pendingOptionalTimeColumns = OPTIONAL_TIME_COLUMNS.filter(
    (column) => !activeManagedColumnKeys.includes(column.key)
  );
  const activeOptionalPayloadCacheColumns = OPTIONAL_PAYLOAD_CACHE_COLUMNS.filter((column) =>
    activeManagedColumnKeys.includes(column.key)
  );
  const pendingOptionalPayloadCacheColumns = OPTIONAL_PAYLOAD_CACHE_COLUMNS.filter(
    (column) => !activeManagedColumnKeys.includes(column.key)
  );
  const activeManageableBaseColumnsForPanel = MANAGEABLE_BASE_COLUMNS.filter((column) =>
    activeManagedColumnKeys.includes(column.key)
  );
  const activeManageableBaseColumnsForTable = MANAGEABLE_BASE_COLUMNS.filter((column) => {
    if (!activeManagedColumnKeys.includes(column.key)) return false;
    if (column.onlyAllDevices && !isAllDevicesView) return false;
    return true;
  });
  const pendingManageableBaseColumns = MANAGEABLE_BASE_COLUMNS.filter(
    (column) => !activeManagedColumnKeys.includes(column.key)
  );
  const activeManagedColumnsForPanel = [
    ...activeManageableBaseColumnsForPanel.map((column) => ({
      ...column,
      panelLabel: column.onlyAllDevices && !isAllDevicesView
        ? `${column.label} (All Devices only)`
        : column.label,
    })),
    ...activeOptionalTimeColumns,
    ...activeOptionalPayloadCacheColumns,
  ];
  const pendingManagedColumnsForPanel = [
    ...pendingManageableBaseColumns.map((column) => ({
      ...column,
      panelLabel: column.onlyAllDevices ? `${column.label} (All Devices only)` : column.label,
    })),
    ...pendingOptionalTimeColumns,
    ...pendingOptionalPayloadCacheColumns,
  ];
  const tableColSpan =
    2 +
    activeManageableBaseColumnsForTable.length +
    activeOptionalTimeColumns.length +
    activeOptionalPayloadCacheColumns.length;
  const moveColumnToActive = (columnKey) => {
    setActiveManagedColumnKeys((prev) =>
      prev.includes(columnKey) ? prev : [...prev, columnKey]
    );
  };
  const moveColumnToPending = (columnKey) => {
    setActiveManagedColumnKeys((prev) => prev.filter((key) => key !== columnKey));
  };
  const viewingLabel = parseAllRetailerDevicesFolderId(selectedFolderId)
    ? `${parseAllRetailerDevicesFolderId(selectedFolderId)} / All Branches`
    : parseAllDevicesFolderId(selectedFolderId)
      ? `${String(activeRetailerName || "").trim() || "Retailer"} / ${String(
        activeBranchName || ""
      ).trim() || "Branch"} / All Devices`
      : selectedFolderId
        ? getResolvedFolderLabel(selectedFolderId)
        : `${String(activeRetailerName || "").trim() || "Retailer"} / ${String(
          activeBranchName || ""
        ).trim() || "Branch"} / All Devices`;

  return (
    <div className="bg-surface font-body text-on-surface flex min-h-screen overflow-hidden">
      <MdmSidebarNav
        showUserManagement={isAdminUser}
        showPermissionManagement={isAdminUser}
        retailerNames={retailerNames}
        allDevices={allDevices}
        expandedRetailers={expandedRetailers}
        expandedBranches={expandedBranches}
        selectedFolderId={selectedFolderId}
        allRetailerDevicesFolderId={allRetailerDevicesFolderId}
        allDevicesFolderId={allDevicesFolderId}
        getBranchKey={getBranchKey}
        toggleRetailer={toggleRetailer}
        toggleBranch={toggleBranch}
        selectRetailerAllDevices={selectRetailerAllDevices}
        selectBranch={selectBranch}
        getRetailerMountedFolders={getRetailerFolders}
        getBranchMountedFolders={getBranchFolders}
        createNewFolder={createNewFolder}
        getFolderKey={getFolderKey}
        getChildFolders={getNestedFolders}
        expandedFolders={expandedFolders}
        toggleFolder={toggleFolder}
        deleteFolder={deleteFolder}
        setExpandedFolders={setExpandedFolders}
        setSelectedFolderId={setSelectedFolderId}
        setMoveTarget={setMoveTarget}
      />

      <main className="flex-1 flex flex-col h-screen relative overflow-hidden bg-surface">
        <header className="w-full h-16 top-0 sticky bg-surface flex items-center justify-between px-6 z-40 shadow-sm">
          <div className="flex items-center space-x-4">
            <button className="md:hidden text-primary">
              <span className="material-symbols-outlined">menu</span>
            </button>
          </div>
          <div className="flex items-center justify-end flex-1 min-w-0">
            <DeviceSearchBar
              value={deviceFilters.searchText}
              onChange={setSearchFilterValue}
              isActive={isSearchFilterActive}
            />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
          <div className="bg-surface-container-lowest rounded-xl shadow-sm overflow-visible flex flex-col">
            <MdmFleetControls
              displayedDevices={filteredSortedDevices}
              exportCurrentViewToExcel={exportCurrentViewToExcel}
              openCommandModal={openCommandModal}
              openFieldCommandModal={openFieldCommandModal}
              showFieldCommandButton={true}
              onRestartCache={restartFullyCache}
              isRestartingCache={isRestartingCache}
              moveTarget={moveTarget}
              setMoveTarget={setMoveTarget}
              isMovingDevices={isMovingDevices}
              retailerNames={retailerNames}
              allDevices={allDevices}
              folderOptions={moveFolderOptions}
              selectedDevicesHomeBranchKey={selectedDevicesHomeBranchKey}
              onMoveSelected={handleMoveSelectedDevices}
              selectedDeviceIds={selectedDeviceIds}
              triggeredSwanSongDeviceIds={triggeredSwanSongDeviceIds}
              onShowSwanSongResults={openSwanSongResultTabs}
              triggeredCameraDevices={triggeredCameraDevices}
              onShowCameraResults={openCameraResultTabs}
              showLocationVersionButton={isBranchLevelFullyView}
              onOpenLocationVersionWindow={openLocationVersionWindow}
              canEditDeviceNotes={canEditDeviceNotes}
              onEditSelectedNotes={() => setIsBulkNotesModalOpen(true)}
              isSavingBulkNotes={isSavingBulkNotes}
              showFieldStatusIndicators={isBranchLevelFullyView}
              fieldStatusCounts={fieldStatusCounts}
              selectedFieldJourneyStatus={selectedFieldJourneyStatus}
              onFieldJourneyStatusFilterToggle={handleFieldJourneyStatusFilterToggle}
              onRefreshFieldStatus={refreshFieldDeviceStatus}
              isRefreshingFieldStatus={isRefreshingFieldStatus}
            />
            <MdmFleetViewContextBar
              viewingLabel={viewingLabel}
              selectedCount={selectedDeviceIds.length}
              columnsPanelRef={columnsPanelRef}
              isColumnsPanelOpen={isColumnsPanelOpen}
              setIsColumnsPanelOpen={setIsColumnsPanelOpen}
              activeManagedColumnsForPanel={activeManagedColumnsForPanel}
              pendingManagedColumnsForPanel={pendingManagedColumnsForPanel}
              moveColumnToPending={moveColumnToPending}
              moveColumnToActive={moveColumnToActive}
              folderActionError={folderActionError}
              foldersError={foldersError}
            />
            <MdmDevicesTable
              allDisplayedSelected={allDisplayedSelected}
              isSelectAllIndeterminate={isSelectAllIndeterminate}
              toggleAllDisplayedSelection={toggleAllDisplayedSelection}
              openColumnFilter={openColumnFilter}
              toggleColumnFilter={toggleColumnFilter}
              deviceFilters={deviceFilters}
              setDeviceFilters={setDeviceFilters}
              closeColumnFilters={closeColumnFilters}
              sortField={sortField}
              sortDirection={sortDirection}
              handleColumnSortToggle={handleColumnSortToggle}
              setDropdownFilterValue={setDropdownFilterValue}
              setRangeFilterValue={setRangeFilterValue}
              setDateRangeFilterValue={setDateRangeFilterValue}
              isSearchFilterActive={isSearchFilterActive}
              setSearchFilterValue={setSearchFilterValue}
              activeManagedColumnKeys={activeManagedColumnKeys}
              isDateFilterActive={isDateFilterActive}
              isBatteryFilterActive={isBatteryFilterActive}
              isStatusFilterActive={isStatusFilterActive}
              isHasIpFilterActive={isHasIpFilterActive}
              isChargingFilterActive={isChargingFilterActive}
              isInKioskFilterActive={isInKioskFilterActive}
              isCurrentFolderFilterActive={isCurrentFolderFilterActive}
              currentFolderFilterOptions={currentFolderFilterOptions}
              activeOptionalTimeColumns={activeOptionalTimeColumns}
              activeOptionalPayloadCacheColumns={activeOptionalPayloadCacheColumns}
              isAllDevicesView={isAllDevicesView}
              isLoading={isLoading}
              fetchError={fetchError}
              tableColSpan={tableColSpan}
              displayedDevices={paginatedDevices}
              totalDevices={totalDevices}
              getStatus={getStatus}
              cleanSsid={cleanSsid}
              selectedDevices={selectedDevices}
              handleDeviceSelectionClick={handleDeviceSelectionClick}
              formatTime={formatTime}
              openDeviceInfoJsonModal={openDeviceInfoJsonModal}
              getBatteryIcon={getBatteryIcon}
              getDuplicateFolderPath={getDuplicateFolderPath}
              getCurrentFolderName={getCurrentFolderName}
              deviceActionResults={deviceActionResults}
              canEditDeviceNotes={canEditDeviceNotes}
              savingNotesDeviceIds={savingNotesDeviceIds}
              onDeviceNotesSave={handleDeviceNotesSave}
            />

            <div className="p-4 bg-surface-container-low/30 flex flex-wrap items-center justify-between gap-3 text-xs font-medium text-on-surface-variant">
              <p>
                {totalFilteredDevices === 0
                  ? totalDevices === 0
                    ? "No devices found"
                    : "No devices match your filters"
                  : `Showing ${pageStartIndex + 1}–${pageEndIndex} of ${totalFilteredDevices} devices`}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-outline-variant/30 bg-surface-container-lowest hover:bg-surface-container-high disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage <= 1}
                    aria-label="Previous page"
                  >
                    <span className="material-symbols-outlined text-sm">chevron_left</span>
                    Previous
                  </button>
                  <span className="px-1 tabular-nums">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-outline-variant/30 bg-surface-container-lowest hover:bg-surface-container-high disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                  >
                    Next
                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <MdmCommandModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        commandScope={commandScope}
        activeCommandOptions={activeCommandOptions}
        commandEndpoint={commandEndpoint}
        setCommandEndpoint={setCommandEndpoint}
        isSendingCommand={isSendingCommand}
        isFieldTriggerConfigUpdateCommand={isFieldTriggerConfigUpdateCommand}
        isFieldTriggerDatabaseUpdateCommand={isFieldTriggerDatabaseUpdateCommand}
        isFieldChangeNcrServerCommand={isFieldChangeNcrServerCommand}
        isFieldFetchNcrStatusCommand={isFieldFetchNcrStatusCommand}
        isFullySettingsCommand={isFullySettingsCommand}
        fullySettingsGroupKey={fullySettingsGroupKey}
        k10GroupKey={k10GroupKey}
        visibleK10Options={visibleK10Options}
        visibleFullySettingsOptions={visibleFullySettingsOptions}
        fullySettingKey={fullySettingKey}
        setFullySettingKey={setFullySettingKey}
        fullySettingValue={fullySettingValue}
        setFullySettingValue={setFullySettingValue}
        fullySettingBooleanValue={fullySettingBooleanValue}
        setFullySettingBooleanValue={setFullySettingBooleanValue}
        isLoadApkFileCommand={isLoadApkFileCommand}
        loadApkUrl={loadApkUrl}
        setLoadApkUrl={setLoadApkUrl}
        fieldDatabaseUpdateCommandKey={FIELD_UPDATE_DATABASE_ENDPOINT}
        fieldDatabaseUpdateOptions={fieldDatabaseUpdateOptions}
        fieldNcrServerOptions={fieldNcrServerOptions}
        selectedNcrServerTarget={selectedNcrServerTarget}
        setSelectedNcrServerTarget={setSelectedNcrServerTarget}
        selectedFieldConfigCategory={selectedFieldConfigCategory}
        setSelectedFieldConfigCategory={setSelectedFieldConfigCategory}
        fieldConfigCategoryOptions={fieldConfigCategoryOptions}
        isFetchingFieldConfig={isFetchingFieldConfig}
        selectedFieldConfigValue={selectedFieldConfigValue}
        setSelectedFieldConfigValue={setSelectedFieldConfigValue}
        selectedFieldConfigValues={selectedFieldConfigValues}
        fieldConfigError={fieldConfigError}
        isFieldTriggerMicromomentCommand={isFieldTriggerMicromomentCommand}
        currentBranchRetailerId={currentBranchRetailerId}
        currentBranchStoreId={currentBranchStoreId}
        selectedMicromomentId={selectedMicromomentId}
        setSelectedMicromomentId={setSelectedMicromomentId}
        micromomentOptions={micromomentOptions}
        isFetchingMicromoments={isFetchingMicromoments}
        micromomentError={micromomentError}
        fieldCommandResponse={fieldCommandResponse}
        isFieldScanAndGoReleaseWallCommand={isFieldScanAndGoReleaseWallCommand}
        isFieldScanAndGoAllowedReleaseCommand={isFieldScanAndGoAllowedReleaseCommand}
        isFieldMultiSwanSongCommand={isFieldMultiSwanSongCommand}
        isFieldTriggerCameraCommand={isFieldTriggerCameraCommand}
        isFieldTriggerScreenshotCommand={isFieldTriggerScreenshotCommand}
        isFieldMultiFetchLocationLogCommand={isFieldMultiFetchLocationLogCommand}
        locationVersionFetchedDeviceIds={locationVersionFetchedDeviceIds}
        onShowLocationVersionWindow={() => openLocationVersionWindow(locationVersionFetchedDeviceIds)}
        triggeredSwanSongDeviceIds={triggeredSwanSongDeviceIds}
        onShowSwanSongResults={openSwanSongResultTabs}
        triggeredCameraDevices={triggeredCameraDevices}
        onShowCameraResults={openCameraResultTabs}
        triggeredScreenshotDevices={triggeredScreenshotDevices}
        allowedReleaseForScanAndGo={allowedReleaseForScanAndGo}
        setAllowedReleaseForScanAndGo={setAllowedReleaseForScanAndGo}
        allowedReleaseNotes={allowedReleaseNotes}
        setAllowedReleaseNotes={setAllowedReleaseNotes}
        isAllowedReleaseNotesRequired={isAllowedReleaseNotesRequired}
        isExecuteDisabled={isAllowedReleaseExecuteBlocked || isK10ExecuteBlocked}
        isK10Command={isK10Command}
        isK10CsdkToggleCommand={isK10CsdkToggleCommand}
        isK10CsdkScheduleCommand={isK10CsdkScheduleCommand}
        isK10ShutdownCommand={isK10ShutdownCommand}
        k10CsdkEnabled={k10CsdkEnabled}
        setK10CsdkEnabled={setK10CsdkEnabled}
        k10CsdkOnStartAppEnabled={k10CsdkOnStartAppEnabled}
        setK10CsdkOnStartAppEnabled={setK10CsdkOnStartAppEnabled}
        k10CsdkScheduleHour={k10CsdkScheduleHour}
        setK10CsdkScheduleHour={setK10CsdkScheduleHour}
        k10CsdkScheduleMinute={k10CsdkScheduleMinute}
        setK10CsdkScheduleMinute={setK10CsdkScheduleMinute}
        k10CsdkScheduleEnabled={k10CsdkScheduleEnabled}
        setK10CsdkScheduleEnabled={setK10CsdkScheduleEnabled}
        nonK10SelectedDevices={nonK10SelectedDevices}
        k10LenovoDeviceModel={K10_LENOVO_DEVICE_MODEL}
        scanAndGoWallOptions={scanAndGoWallOptions}
        selectedScanAndGoWallIds={selectedScanAndGoWallIds}
        toggleScanAndGoWallSelection={toggleScanAndGoWallSelection}
        commandBroadcastTargetLabel={commandBroadcastTargetLabel}
        getTextPayloadKeyForEndpoint={getTextPayloadKeyForEndpoint}
        textCommandOptions={textCommandOptions}
        commandMessageText={commandMessageText}
        setCommandMessageText={setCommandMessageText}
        parsedPayloadDeviceCount={parsedPayloadDeviceCount}
        commandTargetDevices={commandTargetDevices}
        progressDone={progressDone}
        progressTotal={progressTotal}
        progressPercent={progressPercent}
        commandProgress={commandProgress}
        commandUpdatedDevices={commandUpdatedDevices}
        commandSuccessfulDeviceIds={commandSuccessfulDeviceIds}
        showSuccessfulDeviceIds={showSuccessfulDeviceIds}
        setShowSuccessfulDeviceIds={setShowSuccessfulDeviceIds}
        commandFailedDeviceIds={commandFailedDeviceIds}
        showFailedDeviceIds={showFailedDeviceIds}
        setShowFailedDeviceIds={setShowFailedDeviceIds}
        commandError={commandError}
        onExecute={runCommand}
      />

      <MdmDeviceInfoModal
        isOpen={isDeviceInfoModalOpen}
        onClose={closeDeviceInfoModal}
        title={deviceInfoModalTitle}
        tab={deviceInfoModalTab}
        onTabChange={handleDeviceInfoModalTabChange}
        onRefresh={() => handleDeviceInfoModalRefresh()}
        isRefreshingActiveTab={isRefreshingActiveDeviceInfoTab}
        deviceId={deviceInfoModalDeviceId}
        screenshotUrl={deviceInfoModalScreenshotUrl}
        screenshotResult={deviceInfoModalScreenshotResult}
        fieldScreenshotUrl={deviceInfoModalFieldScreenshotUrl}
        fieldScreenshotResult={deviceInfoModalFieldScreenshotResult}
        activeLogResult={deviceInfoModalActiveLogResult}
        parsedInfo={deviceInfoModalParsed}
        locationSource={deviceInfoModalLocationSource}
        onLocationSourceChange={setDeviceInfoModalLocationSource}
        parsedLocation={deviceInfoModalLocationParsed}
        locationLogResult={deviceInfoModalLocationLogResult}
        locationHistory={deviceInfoModalLocationHistory}
        retailerId={deviceInfoModalRetailerId}
        branchId={deviceInfoModalBranchId}
        storeCoordinates={deviceInfoModalStoreCoordinates}
        branchMapData={deviceInfoModalBranchMapData}
        isBranchMapDataLoading={isBranchMapDataLoading}
        branchMapDataError={branchMapDataError}
        isLocationTabActive={deviceInfoModalTab === "location"}
        parsedLog={deviceInfoModalLogParsed}
        infoJson={deviceInfoModalJson}
        logJson={deviceInfoModalLogJson}
        truncateText={truncateText}
        stringifyForTableCell={stringifyForTableCell}
      />

      <MdmLocationVersionWindow
        isOpen={isLocationVersionWindowOpen}
        onClose={closeLocationVersionWindow}
        branchLabel={locationVersionWindowBranchLabel}
        retailerId={currentBranchRetailerId}
        branchId={currentBranchStoreId}
        deviceRows={locationVersionWindowDeviceRows}
        deviceActionResults={deviceActionResults}
        onRefreshMdm={() => refreshLocationVersionMdmDevices(locationVersionWindowDeviceIds)}
        isRefreshingMdm={isRefreshingLocationVersionMdm}
        mdmRefreshError={locationVersionMdmRefreshError}
        onRefreshLogs={() => refreshLocationVersionLogsDevices(locationVersionWindowDeviceIds)}
        isRefreshingLogs={isRefreshingLocationVersionLogs}
        logsRefreshError={locationVersionLogsRefreshError}
        storeCoordinates={locationVersionWindowStoreCoordinates}
        branchMapData={locationVersionWindowBranchMapData}
        isBranchMapDataLoading={isBranchMapDataLoading}
        branchMapDataError={branchMapDataError}
      />

      <MdmDeviceNotesModal
        isOpen={isBulkNotesModalOpen}
        onClose={() => setIsBulkNotesModalOpen(false)}
        deviceIds={selectedDeviceIds}
        isSaving={isSavingBulkNotes}
        onSaveBulk={handleBulkDeviceNotesSave}
      />

    </div>
  );
};

export default MdmLanding;
